/**
 * 家长端 · 目标管理（见契约 1.1）。
 *
 * 设计要点（逐条对应契约）：
 *  1. **只有「目标」**：不再有"任务"这个概念，页面也**不再有目标 / 任务两个 Tab**，
 *     只有**一棵树**。数据接口**不传 `kind`**，后端返回全部节点
 *     （历史上 kind='task' 的老节点就在这棵树里，是普通节点，照常显示）。
 *     ⚠️ `kind` 字段本身**保留在数据里**（数据库列也不动），只是界面不再让用户感知它：
 *     新建出来的节点一律是 `'goal'`。
 *  2. 三级缩进树；每条显示名称、起止日期（长期显示"长期"）、状态、**归属标注**
 *     （家长创建 / 学生创建，来自 `ownershipLabel`）。
 *  3. 新建 / 编辑：截止日期、周期长度、长期**三选一**，周期用 `addPeriod` 折算成 end_date。
 *  4. **时间边界提示**：前端先提示一层（子级超出上级范围），但**以服务端拒绝为准**。
 *  5. **上级缩短期限被拒绝时**：服务端返回越界子项清单，这里明确列出
 *     "这些子项会越界，请先调整它们"，而不是笼统报错。
 *  6. 删除：有打卡记录时服务端拒绝（NODE_HAS_CHECKINS）→ 引导改用**归档**。
 *  7. 学生创建的内容家长可改可删，但标"学生创建"避免误操作；
 *     反过来学生端对家长创建的内容没有入口（见 uiMode.ts 的 canMutateContent）。
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  // ⚠️ 用别名导入：本模块里 `KeyboardEvent` / `MouseEvent` 这两个名字已经被
  // **DOM 全局类型**占用（`RowMenu` 里的 `document.addEventListener('keydown', …)`
  // 用的就是 DOM 的 `KeyboardEvent`）。直接导入 React 的同名类型会把它们遮蔽掉，
  // 那一处的类型立刻就不对了。别名导入两不相扰。
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MAX_NODE_DEPTH,
  addPeriod,
  compareDate,
  describePointsRule,
  formatDateRange,
  isTerminalNodeStatus,
  type Actor,
  type NodeKind,
  type NodeStatus,
} from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { HabitFormModal } from '../../components/parent/HabitFormModal';
/** 打卡任务排序 + 孩子端只读预览（2026-10-31 从「打卡任务总览」页搬到这里）。 */
import { TaskOrderCard } from '../../components/parent/TaskOrderCard';
import { ChildTodayPreview } from '../../components/parent/ChildTodayPreview';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState } from '../../components/ui/Bits';
import { useHabitMutations, useNodeMutations, useNodeTree } from '../../lib/domainQueries';
import { useStudent } from '../../lib/queries';
import { useParentActor } from '../../lib/ActorContext';
import { canCreate, canEditContent, ownershipOf } from '../../lib/uiMode';
import { toErrorMessage } from '../../lib/api/error';
import { parseOutOfRangeChildren, describeDetails } from '../../lib/api/shape';
import { ApiError } from '../../lib/api/error';
import { cn, formatDateTimeLocal } from '../../lib/utils';
import { nodeFilterKeys } from '../../lib/nodeStatusFilter';
import type { Habit, NodeSummary, NodeWritePayload } from '../../lib/api/types';

/**
 * 新建 / 编辑时唯一的"类型"—— 只有目标。
 *
 * 页面已经不再有目标 / 任务两个 Tab（`kind` 不再约束任何东西，见文件头注释），
 * 所以新建出来的节点固定用 `'goal'`；用户可见的文案里也不会再出现"任务"这个说法。
 */
const NODE_LABEL = '目标';

/**
 * 状态 → 标签 / 色调（覆盖**全部四个**状态，`Record<NodeStatus, …>` 会强制这一点）。
 *
 * 标签口径与共享常量 `NODE_STATUS_LABEL` 一致，只在"已完成 / 已停止"这两处补一个"已"字
 * —— 枚举里的写法是给 chip、导出等短场景用的（"完成"/"停止"），
 * 而正文里的徽章写成"已完成"读起来更像状态而不是动作。
 *
 * ⚠️ **归档不在这个表里**：`archived` 已经不是状态了，而是独立字段
 * `node.archivedAt`。已归档要单独用一个标记显示，不能混进状态标签 ——
 * 否则"已归档 + 已完成"这种组合根本表达不出来。
 */
const STATUS_META: Record<
  NodeStatus,
  { label: string; tone: 'neutral' | 'brand' | 'success' | 'warn' | 'danger' }
> = {
  active: { label: '进行中', tone: 'success' },
  paused: { label: '暂停', tone: 'neutral' },
  completed: { label: '已完成', tone: 'brand' },
  stopped: { label: '已停止', tone: 'warn' },
};

/** 是否已归档。**只看 `archivedAt`**，不要找 `status === 'archived'`（那个取值已不存在）。 */
function isArchived(node: NodeSummary): boolean {
  return node.archivedAt !== null;
}

// ---------------------------------------------------------------- 视觉层级
//
// 视觉手段**各管一件事**（不拿一个手段表达两件事），而且**只在需要区分时才出现**：
//   层级序号圆标 + 行底色（色相）→ 层级；
//   引导线 + 缩进 → 从属关系；
//   颜色（左侧色条 + 状态文字）→ **只标例外**（暂停 / 完成 / 停止）。
//
// 「层级」只用**两个互相配合的手段**，而不是三个：
//   · **层级序号圆标**（1 / 2 / 3）：最强的定位手段，扫一眼就知道
//     "这一屏里哪些是第 1 级、哪些是第 3 级"，而且**不用去数缩进**；
//   · **行底色**（每级一个明亮色相）：整行铺开，让"块"本身就能区分。
// 两者都从**同一张表 `LEVELS`** 取值（唯一入口是 `levelStyle`）—— 一张表同时给出
// 圆标 / 底色，就不会出现"底色调了、圆标没跟上"的错位，也不会再犯
// "同一概念在多处各写一套"的错。
// ⛔ **标题不再承担层级**：三层的标题字级**完全一致**（见 `LEVEL_TITLE`）。
//   上一版给三层各写一套字号 / 字重 / 颜色，结果是"一级很醒目、二三级不好找"——
//   标题一旦随层级变化，"读得清"就要看运气。层级交给圆标 + 底色已经足够。
// ⚠️ 色相的选择是刻意的：三层只用 `indigo` / `sky` / `amber`，并且**刻意避开
//   绿系与红系（`emerald` / `green` / `rose` / `red`）** —— 它们已经被**状态**占满了
//   （完成 = emerald、停止 = rose，见 `STATUS_ACCENT`）。层级若也用这两个色相，
//   同一条行上就会出现两套颜色语义打架（"这块偏绿是因为它是第 2 级，
//   还是因为已经完成？"）。品牌蓝 `brand` 同理避开：它是**可交互元素**
//   （链接 / 按钮 / 筛选 chip）的专用色。
//
// ⛔⛔ **不要再靠"把低层级标题调淡"来表达层级**（上一版就是这么做的，已被用户否掉）。
// 上一版把第 2 层写成 `font-medium text-slate-800`、第 3 层写成 `font-normal text-slate-700`
// 并且第 3 层底色用白色，理由是想让层级"可辨"—— 结果恰恰相反：
// **二、三级标题变得不好找**（用户反馈："对二、三级目标的标题就是那么好找"，
// 即"一级很醒目、二三级不好找"）。正确的杠杆是：
//   **层级由"醒目的圆标 + 明亮底色"表达，所有标题都同样清晰可读 —— 标题不参与层级。**
// 换句话说：层级要"往上加东西"（圆标 / 带色相的底），而不是"往下减东西"（更淡的字）。
//
// ⚠️ 本轮删掉了两样东西，别再请回来：
//  1. **层级形状图标**（圆环 / 菱形 / 圆点）：形状本身不携带意义，用户不会去记
//     "菱形 = 里程碑"；而层级已经由缩进 + 带色相的底色 + 序号圆标表达了 ——
//     图标在做一件已经有人的事。
//  2. **"进行中"的蓝色色条**：所有目标默认都是进行中，满屏蓝条不提供任何信息，
//     只是让页面变花。颜色应该标记例外，而不是常态。
// （打卡任务行的「循环箭头」图标**保留**：它表达"这是周期性的"，是真信息。）

/**
 * **唯一一处**「状态 → 颜色」映射。左侧色条与状态文字都从它取色，
 * 以后要调色只改这里 —— 这个仓库已经因为"同一概念多处实现"踩过坑。
 *
 * `bar: null` 表示**这个状态不画色条**（只有 `active` 一个）：见上面的注释。
 * `text` 就是状态文字的颜色 —— `active` 刻意用**中性灰**而不是品牌蓝，理由同上：
 * 蓝色是常态色，一旦常态上色，"蓝色满屏"就又回来了。
 *
 * ⚠️ **归档不在这个表里**：归档是独立字段（`archivedAt`），表达方式是"整行降透明度"，
 * 而不是第五种颜色 —— 否则"已归档 + 已完成"这种组合又表达不出来了。
 */
const STATUS_ACCENT: Record<NodeStatus, { bar: string | null; text: string }> = {
  active: { bar: null, text: 'text-slate-500' },
  paused: { bar: 'bg-slate-400', text: 'text-slate-400' },
  completed: { bar: 'bg-emerald-500', text: 'text-emerald-500' },
  stopped: { bar: 'bg-rose-500', text: 'text-rose-500' },
};

/**
 * 行左侧状态色条的类名（4px 圆角竖条，只在"非进行中"时看得见）。
 *
 * **为什么无条时用"等宽透明占位"而不是让内容左移**：同一层级的兄弟行常常状态不一
 * （默认筛选就是「进行中 + 暂停」并排），若进行中的行整体左移 12px，同一棵子树里
 * 各行文字的左边缘就会参差不齐 —— 那正是"分不清谁属于谁"的观感来源之一。
 * 占位保证**所有行的文字左边缘完全对齐**，代价只是左侧多 12px 留白。
 * 占位是 `bg-transparent`：看不见、不占语义，不是"灰色的条"。
 *
 * `self-stretch` 让它跟整行一样高（行容器是 `items-start`，需要单独拉满）。
 * 已归档时色条跟着整行一起再压暗一点：行都灰了、色条还很亮会很刺眼。
 */
function statusBarClass(status: NodeStatus, archived: boolean): string {
  return cn(
    'w-1 shrink-0 self-stretch rounded-full',
    // 无条时的占位：这个字面量 `bg-transparent` 就写在源码里，Tailwind 必然生成它
    STATUS_ACCENT[status].bar ?? 'bg-transparent',
    archived && 'opacity-70',
  );
}

/**
 * 三层**共用**的标题字级 —— 刻意**不写进 `LEVELS`**。
 *
 * 理由：**标题不再承担层级**（层级只由「序号圆标 + 行底色」表达），所以它
 * **不该随层级变化**。写进 `LEVELS` 就意味着"三层各写一份相同的字符串" ——
 * 那种写法迟早会在某一次调字号 / 调颜色时被改歪一两层，变成"二三级又不好找了"。
 * 一个不随层级变化的样式，就应该是一个**共享常量**，而不是三份重复。
 *
 * ⚠️ 它只管**节点标题**（`node.name`）：打卡任务子行的 `HABIT_TEXT` 与全部次级信息
 * （状态文字 / 徽章 / 「第 N 级」/ 日期 / 进度条）都不受它影响。
 */
const LEVEL_TITLE = 'text-sm font-semibold text-slate-900';

/**
 * `LEVELS` —— **整个页面里唯一一处**「层级 → 样式」映射。
 *
 * 每个层级一条记录，同时给出**两样**东西；以后要调层级的观感，**只改这张表**：
 *  - `row`  ：**节点行本身**的底色（每级一个明亮色相）；
 *  - `badge`：标题**前面**那个**层级序号圆标**（正圆 + 层级数字 1/2/3）。
 *            它是"一眼找到某一级"的**主要手段**，行底色是它的辅助。
 *
 * ⛔⛔ **`habit` 字段本轮已从这张表里删掉**（这是个真 bug 的修复，别再请回来）：
 *   它曾经是"每级再浅一档"的打卡任务行底色（`*-50`），于是**同一个"打卡任务"概念**
 *   挂第 2 级下面是 `sky-50`、挂第 3 级下面是 `amber-50` —— 用户一眼就看出来
 *   "2 级目标下面的打卡任务和 3 级目标下面的打卡任务底色不一样？要统一一下"。
 *   教训（这是本次修复的核心）：**一个东西的身份不该通过"它父级是谁"来表达**。
 *   打卡任务行的底色现在有**它自己的、唯一的一处来源**：`HABIT_ROW_BG`（见下方）。
 *
 * ⛔ **`title` 不在这张表里**：三层标题完全一致，见上面的 `LEVEL_TITLE` ——
 *  一个不随层级变化的东西放进"层级 → 样式"表，只会退化成三份重复字符串。
 *
 * 配色（本轮按用户要求从灰系换成**明亮色系**）：
 *   1 级 `indigo`、2 级 `sky`、3 级 `amber`；圆标用实心色块（`500 / 500 / 400`），
 *   行底色统一 `*-100`。
 *   三个色相明显不同，"这是第几级"不用数缩进、也不用读文字就能看出来。
 *
 * ⛔⛔ **三层的色相刻意避开绿系与红系**（`emerald` / `green` / `rose` / `red`）：
 *   它们已经被**状态**占用（完成 = emerald、停止 = rose，见 `STATUS_ACCENT`），
 *   层级再用同一批色相，"这块绿是层级还是完成"就说不清了。
 *   品牌蓝 `brand` 同理避开：它是**可交互元素**（链接 / 按钮 / 筛选 chip）的专用色。
 *   ⚠️ `amber` 与"学生创建"徽章（`Badge tone="warn"` = `bg-amber-50 text-amber-700`）
 *   以及打卡任务行的积分数（`text-amber-600`）同属一个暖色家族。第 3 级的行底色是
 *   `bg-amber-100`，压在这两个既有元素下面仍然是"浅底 + 深字"，读得清；
 *   而**这两处次级信息的样式一个字都不动**（它们是既有语义色，不属于层级）。
 *
 * ⚠️ **底色只上在"行本身"，不铺满整棵子树**，而且**全部不透明**（绝不写 `/60`、`/30`）：
 *  如果底色铺满子树、或者用半透明，内层底色就会叠在外层底色上，第 2、3 层会一起变深、
 *  看起来一模一样，层级就白排了（上一版正是踩了这个坑）。
 *  三层的行底色**互不嵌套**（子行的底是它自己的 `row`，不是叠在父行上），
 *  所以 `indigo-100` / `sky-100` / `amber-100` 在三种位置上都是同一个色值。
 *  （唯一的半透明叠加在**下钻行的 hover / active** 上，那是交互反馈、不表达层级，见 `DrillRow`。）
 *
 * ⚠️ **圆标不承担状态语义**：它是层级锚点，不是状态灯。三层都用各自色相的实心块
 *  （1、2 级白字，3 级 `amber-400` 配 `amber-900` 深字，保证数字读得清）。
 */
const LEVELS: Record<1 | 2 | 3, { row: string; badge: string }> = {
  1: {
    row: 'bg-indigo-100',
    /**
     * 圆标底色按**可读性**挑色阶，不是按"哪个好看"：
     * 里面那个数字是"一眼认出层级"的唯一锚点，看不清就白做了。
     * 所以每一级都取**与该级文字色对比度 ≥ 4.5:1**（WCAG AA，正文级）的色阶：
     *   1 级 indigo-600 + 白字 ≈ 6.5:1
     *   2 级 sky-700   + 白字 ≈ 5.5:1   ← 原先的 sky-500 只有 2.77:1，数字发虚
     *   3 级 amber-500 + amber-950 深字 ≈ 8:1  ← 琥珀太亮，白字永远不达标，故改用深字
     * 顺带：圆标与**本行底色**的对比也一并提高（3 级的 amber-400 on amber-100 只有 1.5:1，
     * 圆几乎看不出来）。
     */
    badge: 'bg-indigo-600 text-white',
  },
  2: {
    row: 'bg-sky-100',
    badge: 'bg-sky-700 text-white',
  },
  3: {
    row: 'bg-amber-100',
    badge: 'bg-amber-500 text-amber-950',
  },
};

/**
 * **打卡任务行的底色** —— 全页面**唯一**一处来源（从前分散在 `LEVELS[*].habit` 里）。
 *
 * 取值 `bg-white`，理由：**打卡任务行的底色必须对三种父级都成立**。
 * 父级行底分别是 `indigo-100` / `sky-100` / `amber-100`（都是带色相的明亮底），
 * 而白色与这三者都有明确区分 —— 于是"2 级下面的打卡任务"与"3 级下面的打卡任务"
 * 终于长得一样了（这正是用户报的那个 bug）。也**刻意不选 `bg-slate-50`**：
 * 灰白压在 `sky-100` / `indigo-100` 上会发灰发脏，而纯白不引入任何新色相，
 * 对三种父级是**同一个绝对色值**，不需要"每级各挑一次"。
 *
 * ⛔⛔ **不要再让它随父级层级变化**（本次 bug 的教训）：
 *   **一个东西的身份不该通过"它父级是谁"来表达。**
 *   打卡任务就是打卡任务 —— 挂在第 2 级下面还是第 3 级下面，它是同一个东西、同一个底色。
 *
 * ⚠️ 那它凭什么是"子行"？靠三件**与父级无关**的事（都在 `NodeHabitLines` 里）：
 *   1. **更细的左侧引导线**（1px，节点层级之间是 2px）；
 *   2. **缩进**（挂在节点之下，宽屏 `ml-6`、窄屏 `pl-1`）；
 *   3. **更小的字号**（`HABIT_TEXT`，比第 3 级的标题再轻一档）。
 *   也就是说：**从属关系由"结构"表达，不由"底色跟着父级变"表达** ——
 *   底色只回答"我是哪一类东西"，不回答"我爹是谁"。
 */
const HABIT_ROW_BG = 'bg-white';

/** 把任意 `depth` 收拢到 1–3（脏数据超出范围时按第 3 层处理，与"最多三级"的约束一致）。 */
function levelOf(depth: number): 1 | 2 | 3 {
  if (depth <= 1) return 1;
  if (depth === 2) return 2;
  return 3;
}

/**
 * 取某层级的整套样式（行底色 + 标题字级 + 层级序号圆标）。
 *
 * ⚠️ **唯一入口**：行底色、标题字级、**序号圆标**都必须走这里，
 * 页面里任何地方都不要再手写 `bg-indigo-100` / `font-semibold text-slate-900` /
 * `bg-sky-500 text-white` 之类的层级类名
 * —— 这就是"层级样式只有一处定义"的落地方式。
 *
 * ⛔ **打卡任务行的底色不从这里取**（本轮改动）：它不属于"层级样式"，
 *   而是 `HABIT_ROW_BG` 那个独立常量 —— 见那里的注释（"身份不由父级表达"）。
 *
 * ⚠️ 返回的 `title` **三层是同一个常量**（`LEVEL_TITLE`），不是梯度：
 * 标题不再承担层级（理由见 `LEVEL_TITLE` 与文件上方）。它仍然从这里返回，
 * 只是为了让调用方**只认这一个入口**、不必自己去找第二处常量。
 *
 * 窄屏下钻与宽屏树形**共用**这一个函数，所以同一条行在两种形态下的样式完全一致
 * （下钻一次只显示一层，而同一层的行都是兄弟、`depth` 相同，因此一屏之内的底色依旧统一，
 * 不会变成"深浅不一的块"；圆标则恒定显示"我现在在第几级"，这正是下钻形态最需要的定位信息）。
 */
function levelStyle(depth: number): {
  row: string;
  title: string;
  badge: string;
} {
  return { ...LEVELS[levelOf(depth)], title: LEVEL_TITLE };
}

/**
 * **打卡任务子行**的文字档位：比第 3 层的标题再轻一档（更小、更浅）。
 * 它不属于"节点层级"（打卡任务不是第四级），所以单独一个常量，不塞进 `LEVELS`。
 * 子行内的名称保留自己的 `font-medium`（那是行内主次，不是层级梯度），其余灰字不受影响。
 *
 * ⚠️ 与它成对的另一个"打卡任务行自有样式"是 `HABIT_ROW_BG`（底色）——
 * 两者是同一层意思（**打卡任务这一类行自己的样子**），都不从 `LEVELS` 取。
 */
const HABIT_TEXT = 'text-xs text-slate-600';

/** 判断某个节点的**子孙**里有多少个会被"向下传播"的动作影响到。 */
function countDescendants(
  node: NodeSummary,
  predicate: (descendant: NodeSummary) => boolean,
): number {
  let count = 0;
  const walk = (items: NodeSummary[]) => {
    for (const item of items) {
      if (predicate(item)) count++;
      if (item.children && item.children.length > 0) walk(item.children);
    }
  };
  walk(node.children ?? []);
  return count;
}

/**
 * 把树拍平成"浅层副本"列表（children 置空，但保留 parentId / depth / 其它字段）。
 *
 * 为什么需要它：筛选 chips 是多选、"叠加在同一列表上"的 —— 若先按状态裁掉树里的
 * 节点再渲染，父子关系会断。正确做法是：先按筛选挑出**命中**的节点，再按 `parentId`
 * 把它们的祖先还原回来当上下文，这样被筛掉的父目标仍然可见、缩进层级不乱。
 */
function flattenNodes(nodes: NodeSummary[]): NodeSummary[] {
  const out: NodeSummary[] = [];
  const walk = (items: NodeSummary[]) => {
    for (const node of items) {
      out.push({ ...node, children: [] });
      if (node.children && node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/**
 * 排序口径与 `lib/api/nodes.ts` 的 `sortTree` 一致：先 `sortOrder`，再按中文名。
 * 不一致的话，筛选一次之后同一层的顺序会和后端给的顺序不一样，
 * 看起来就像"内容自己换了位置"。
 */
function sortNodes(items: NodeSummary[]): NodeSummary[] {
  const sorted = [...items].sort((a, b) => {
    const orderA = a.sortOrder ?? 0;
    const orderB = b.sortOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name, 'zh-Hans-CN');
  });
  for (const node of sorted) {
    if (node.children && node.children.length > 0) node.children = sortNodes(node.children);
  }
  return sorted;
}

/** 把拍平后的列表按 parentId 重新组装成树（只保留能连到根的链）。 */
function rebuildTree(flat: NodeSummary[]): NodeSummary[] {
  const byId = new Map<string, NodeSummary>();
  for (const node of flat) byId.set(node.id, { ...node, children: [] });

  const roots: NodeSummary[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children = [...(parent.children ?? []), node];
    else roots.push(node);
  }
  return sortNodes(roots);
}

/**
 * 在树里按 id 找节点（窄屏下钻用：确认"当前焦点节点"在当前筛选结果里还在不在）。
 * 找到即返回，找不到返回 `null` —— 调用方据此落回根。
 */
function findNode(items: NodeSummary[], id: string): NodeSummary | null {
  for (const node of items) {
    if (node.id === id) return node;
    const hit = node.children && node.children.length > 0 ? findNode(node.children, id) : null;
    if (hit) return hit;
  }
  return null;
}

/**
 * 从根到目标节点的**路径**（面包屑的数据结构）。
 *
 * 返回的是**节点数组**（不是名字数组）：每一段都带着自己的 id，
 * 于是"点哪一段就回到哪一层"直接用 `setDrillFocusId(crumb.id)` 就行。
 * 不含目标节点本身（当前层由调用方单独渲染成普通文字）。
 * 目标不在树里时返回空数组。
 */
function trailOf(items: NodeSummary[], targetId: string): NodeSummary[] {
  const walk = (list: NodeSummary[], acc: NodeSummary[]): NodeSummary[] | null => {
    for (const node of list) {
      if (node.id === targetId) return acc;
      if (node.children && node.children.length > 0) {
        const hit = walk(node.children, [...acc, node]);
        if (hit) return hit;
      }
    }
    return null;
  };
  return walk(items, []) ?? [];
}

// ---------------------------------------------------------------- 筛选 chips

/**
 * 筛选维度：四个状态 + 独立的「已归档」。
 *
 * ⚠️ 这里刻意用**多选 chips**而不是 Tab：Tab 会把数据切碎（一次只能看一类，
 * 无法一眼看到全局，而且切来切去会丢失上下文）；筛选是叠加在同一棵树上的，
 * 选几类就同时看几类。
 */
type StatusFilter = NodeStatus | 'archived';
type FilterChip = StatusFilter | 'all';

const FILTER_CHIPS: Array<{ value: FilterChip; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'active', label: '进行中' },
  { value: 'paused', label: '暂停' },
  { value: 'completed', label: '完成' },
  { value: 'stopped', label: '停止' },
  { value: 'archived', label: '已归档' },
];

/**
 * 默认只选「进行中」（2026-10-30 用户要求：默认不要把「暂停」一起选上）。
 *
 * 完成 / 停止 / 已归档 / 暂停都默认折叠起来，需要时再叠加勾选。
 */
const DEFAULT_FILTERS: StatusFilter[] = ['active'];

/**
 * 一个节点属于哪些筛选维度。
 *
 * ⚠️ **归档优先且互斥**（2026-10-30 修正，用户报的 bug）：
 * > "'强身健体'我已经归档了，上面筛选我也没有选'已归档'，为什么这一块还显示？"
 *
 * 旧写法是 `[node.status, ...(archived ? ['archived'] : [])]` —— 已归档但状态仍是
 * `active` 的节点会**同时**算进「进行中」和「已归档」，于是默认筛选（进行中+暂停）
 * 永远把它带回来，归档等于没生效；计数也会出现 `进行中(16) + 已归档(1)` 这种
 * "16 个节点、数出 17" 的矛盾。
 *
 * 判据在 `lib/nodeStatusFilter.ts`（纯函数，有单测）—— 这里只是入口。
 */
function filterKeysOf(node: NodeSummary): StatusFilter[] {
  return nodeFilterKeys(node);
}

// ---------------------------------------------------------------- 状态动作文案

type PropagatingAction = 'pause' | 'resume' | 'stop' | 'reopen';

/**
 * 确认弹窗的文案表。**每个动作都要用一句人话说清"会顺带改掉什么"** ——
 * 只说"确定要暂停吗"等于没说，家长根本不知道孩子那边的目标会一起变。
 */
const STATUS_CONFIRM: Record<
  PropagatingAction,
  { title: string; confirm: string; impact: string; detail: string }
> = {
  pause: {
    title: '暂停确认',
    confirm: '确认暂停',
    impact: '其下未完成的子目标也会暂停（已完成的保持不变）。',
    detail: '暂停只是先停下来：孩子端不再出现新的打卡，随时可以「恢复」。',
  },
  resume: {
    title: '恢复确认',
    confirm: '确认恢复',
    impact: '会一并恢复之前被这次操作暂停的子目标。',
    detail: '你自己手动暂停过的子目标不会被顺手打开 —— 只有"被这次暂停带下去的"会回到进行中。',
  },
  stop: {
    title: '停止确认',
    confirm: '确认停止',
    impact: '其下未完成的子目标也会停止（已完成的保持不变）。',
    detail: '停止是终态但**可逆**：需要时还能「重新开启」回到进行中。',
  },
  reopen: {
    title: '重新开启确认',
    confirm: '确认重新开启',
    impact: '会一并恢复之前被这次操作暂停/停止的子目标。',
    detail: '这条会回到「进行中」，孩子端重新出现对应的打卡。',
  },
};

/** 动作成功后的 Toast 文案（"已暂停「xxx」"）。 */
const STATUS_ACTION_DONE: Record<PropagatingAction | 'complete', string> = {
  pause: '已暂停',
  resume: '已恢复',
  complete: '已完成',
  stop: '已停止',
  reopen: '已重新开启',
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: '周日' },
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
] as const;

const RECURRENCE_LABEL: Record<string, string> = {
  daily: '每天',
  weekly: '每周',
  monthly: '每月',
  custom: '自定义',
};

/** 把一条 habit 的频率描述成一句人话。 */
function describeRecurrence(habit: {
  recurrence: string;
  weekdays: number[];
  monthDays: number[];
  intervalDays: number | null;
}): string {
  if (habit.recurrence === 'weekly') {
    const days = habit.weekdays
      .slice()
      .sort((a, b) => a - b)
      .map((d) => WEEKDAY_OPTIONS.find((o) => o.value === d)?.label.slice(1) ?? d)
      .join('、');
    return `每周${days ? ` ${days}` : ''}`;
  }
  if (habit.recurrence === 'monthly') {
    const days = habit.monthDays
      .slice()
      .sort((a, b) => a - b)
      .map((d) => (d === -1 ? '最后一天' : `${d} 日`))
      .join('、');
    return `每月 ${days}`;
  }
  if (habit.recurrence === 'custom') return `每 ${habit.intervalDays ?? 1} 天`;
  return RECURRENCE_LABEL[habit.recurrence] ?? habit.recurrence;
}

// ---------------------------------------------------------------- 表单

interface NodeFormState {
  name: string;
  note: string;
  startDate: string;
  /** 三选一：'end' | 'period' | 'long' */
  mode: 'end' | 'period' | 'long';
  endDate: string;
  years: string;
  months: string;
  days: string;
  parentId: string;
}

function emptyForm(kind: NodeKind, today: string, parentId: string | null): NodeFormState {
  return {
    name: '',
    note: '',
    startDate: today,
    mode: kind === 'goal' ? 'long' : 'end',
    endDate: today,
    years: '0',
    months: '0',
    days: '30',
    parentId: parentId ?? '',
  };
}

/** 表单 → 接口入参。周期长度用 shared 的 addPeriod 折算，前端与后端同一套算法。 */
function formToPayload(form: NodeFormState): NodeWritePayload {
  const base: NodeWritePayload = {
    name: form.name.trim(),
    note: form.note.trim(),
    startDate: form.startDate,
  };
  if (form.mode === 'long') {
    return { ...base, isLongTerm: true, endDate: null, period: null };
  }
  if (form.mode === 'end') {
    return { ...base, isLongTerm: false, endDate: form.endDate, period: null };
  }
  const period = {
    years: Number(form.years) || 0,
    months: Number(form.months) || 0,
    days: Number(form.days) || 0,
  };
  return {
    ...base,
    isLongTerm: false,
    period,
    // 派生截止日期也一并送上去，服务端可以只用其中一个字段
    endDate: addPeriod(form.startDate, period),
  };
}

/** 编辑既有节点时把库里的字段还原成表单（保留原始"周期长度"用于回显）。 */
function nodeToForm(node: NodeSummary): NodeFormState {
  const hasDuration =
    (node.durationYears ?? 0) > 0 || (node.durationMonths ?? 0) > 0 || (node.durationDays ?? 0) > 0;
  const mode: NodeFormState['mode'] = node.isLongTerm
    ? 'long'
    : hasDuration
      ? 'period'
      : 'end';
  return {
    name: node.name,
    note: node.note,
    startDate: node.startDate,
    mode,
    endDate: node.endDate ?? node.startDate,
    years: String(node.durationYears ?? 0),
    months: String(node.durationMonths ?? 0),
    days: String(node.durationDays ?? 0),
    parentId: node.parentId ?? '',
  };
}

// ---------------------------------------------------------------- 行内操作

/**
 * 菜单 id 空间约定（**重要，别绕开它**）。
 *
 * 页面上只有**一个** `openMenuId` 状态，节点菜单与打卡任务菜单共用它 ——
 * 这正是"同一时刻屏幕上只允许一个浮层菜单"的实现方式（打开打卡任务的菜单时
 * 节点的菜单会自动关掉，反过来也一样）。
 *
 * 共用状态就必须让两边的 id 撞不上，所以统一加前缀：
 *  - 节点菜单：`n:<nodeId>`
 *  - 打卡任务菜单：`h:<habitId>`
 *
 * 前缀让两个 id 空间天然不相交（即使以后 id 生成方式变了，也不会出现
 * "节点和它下面的某条打卡任务恰好同 id → 两个菜单一起开着"这种怪事）。
 * ⚠️ 以后再加第三种菜单，请沿用这个约定并选一个新前缀。
 */
const nodeMenuId = (nodeId: string) => `n:${nodeId}`;
const habitMenuId = (habitId: string) => `h:${habitId}`;

/**
 * 菜单面板**只在当前断点的那一份行里渲染**（`'wide'` = 桌面树形，`'narrow'` = 窄屏下钻）。
 *
 * 为什么必须有这个（否则是个真 bug）：本轮把"树形 / 下钻"两份渲染**都放进 DOM**、
 * 用 `md:hidden` / `hidden md:block` 控制显示。但**菜单面板本身是 `position: fixed`**
 * —— 它是脱离正常流的，父级容器上的 `display:none` 对它**不生效**，
 * 于是同一行会有两个 `fixed` 面板同时可见（一份来自树、一份来自下钻列表）。
 * 所以这里靠"只有当前断点那一份渲染面板"来保证**同一时刻屏幕上只有一个菜单**，
 * 而不是指望 CSS 去藏一个 `fixed` 元素。
 *
 * ⚠️ 面板里的菜单项、分组、确认弹窗、`n:`/`h:` id 约定、点外关闭 / Esc / 方向键
 * 全部不受影响 —— 这里只决定"哪一个断点的那一份渲染面板"。
 */
const MenuVariantContext = createContext<'wide' | 'narrow'>('wide');

/** 读当前行属于哪一种形态（见 `MenuVariantContext`）。 */
function useMenuVariant(): 'wide' | 'narrow' {
  return useContext(MenuVariantContext);
}

/** 菜单项渲染器：由 `RowMenu` 把"先关菜单再执行"的包装器交给各组菜单项使用。 */
type MenuItemRenderer = (pick: (action: () => void) => () => void) => ReactNode;

/** 菜单项：`danger` 只用于删除（红色文字，与其它项区分开）。 */
function NodeMenuItem({
  onSelect,
  danger = false,
  children,
}: {
  onSelect: () => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        // 高度 ≥36px、整行可点，手机上有足够的点按区域
        'flex min-h-[36px] w-full items-center px-3 py-2 text-left text-sm font-medium transition-colors',
        danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50',
      )}
    >
      {children}
    </button>
  );
}

/** 分组之间的分隔线（新建 / 状态 / 管理 / 危险 各组之间）。 */
function NodeMenuSeparator() {
  return <div className="my-1 h-px bg-slate-100" role="none" />;
}

/**
 * 「⋯」菜单的**外壳**：触发器 + 浮层面板 + 关闭机制。
 *
 * 节点行与**每条打卡任务行**共用这一套（不是各写一份）—— 打开/关闭的行为
 * 只实现一次，两处就不会慢慢跑偏：
 *  - 触发器的 `aria-expanded` 决定开合（受控，见页面的 `openMenuId`）；
 *  - 点外部 / Esc 关闭，↑/↓/Home/End 在菜单项间移动焦点；
 *  - 菜单项统一走 `pick`：**先关菜单，再执行动作**（确认弹窗照旧由页面弹出）。
 *
 * 无障碍：触发器带 `aria-haspopup="menu"` / `aria-expanded`，面板是 `role="menu"`
 * 且各项为 `role="menuitem"`。
 */
function RowMenu({
  open,
  label,
  onToggle,
  onClose,
  children,
}: {
  open: boolean;
  /** 无障碍命名用：触发器读作"更多操作：xxx"，面板读作"「xxx」的操作"。 */
  label: string;
  onToggle: () => void;
  onClose: () => void;
  children: MenuItemRenderer;
}) {
  /** 这一份行属于哪一种形态 —— 只有当前断点的那一份渲染面板（见 `MenuVariantContext`）。 */
  const variant = useMenuVariant();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /**
   * 菜单面板必须落在 `position: fixed` 上：节点行嵌在树里，祖先可能带
   * `overflow` / stacking context（卡片、缩进容器），`absolute` 容易被裁掉。
   * 用 `fixed` + 一个很小的安全边距，菜单就不会被任何祖先裁掉，
   * 也能保证在窄屏上不超出屏幕右边缘。
   */
  const [position, setPosition] = useState<{ top: number; left: number; maxWidth: number } | null>(
    null,
  );

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      // 兜底 360：极端情况（拿不到 innerWidth，例如测试环境）也给出一个有限宽度
      const viewportWidth = window.innerWidth || 360;
      const margin = 8;
      const width = Math.min(208, viewportWidth - margin * 2);
      setPosition({
        // 菜单从触发器下方展开（`top` 是 fixed 定位，不受滚动容器影响，无需加 scrollY）
        top: rect.bottom + 4,
        left: Math.min(Math.max(rect.right - width, margin), viewportWidth - width - margin),
        maxWidth: width,
      });
    };
    update();
    // 滚动 / 旋转屏幕时跟着触发器走，避免菜单悬在原地
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  /**
   * 点击外部关闭。
   *
   * ⚠️ 两个必须同时成立的细节（否则会出现"打开的那次点击立刻把它关掉"）：
   *  1. 这个 effect **只在 `open` 为 true 时才注册监听**，而 `open` 由点击后的
   *     state 更新驱动 —— React 的点击处理发生在本次事件派发**结束之后**的
   *     渲染阶段，所以注册时那次点击早已派发完毕，不会回头命中这个监听；
   *  2. 用 `pointerdown` 而不是 `click`：按下即关，比等一次完整的点击更跟手，
   *     同时它的目标判定只看"按在哪里"，不会把菜单项自己的点击当成"外部"。
   *     （`Modal.tsx` 的遮罩用的是同一套 `pointerdown` 思路，见那里的注释。）
   */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return; // 触发器自己负责开/关切换
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onClose]);

  // Esc 关闭；↑/↓/Home/End 在菜单项之间移动焦点（键盘可用）
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement as HTMLElement);
      const focusAt = (next: number) => {
        event.preventDefault();
        items[(next + items.length) % items.length]?.focus();
      };
      if (event.key === 'ArrowDown') focusAt(index + 1);
      else if (event.key === 'ArrowUp') focusAt(index - 1);
      else if (event.key === 'Home') focusAt(0);
      else if (event.key === 'End') focusAt(items.length - 1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // 打开后把焦点移进菜单，让键盘用户接得上
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  /** 菜单项统一出口：先收菜单，再把动作交回页面（确认弹窗照旧弹出）。 */
  const pick = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <div
      className={cn(
        'flex shrink-0 items-center',
        /*
          ⚠️ `absolute` **只在菜单打开时**加上，而且只在**窄屏**生效（`md:static` 把它在
          宽屏关掉）。原因：在"行本身是一个整块按钮"的窄屏下钻形态里，触发器如果占位，
          "进入提示"就会被挤走。让它脱离流之后：窄屏下钻行的「⋯」固定在行的右上角，
          **进入提示**铺满整行宽度。
          为什么是 `absolute` 而不是 `fixed`：菜单面板本身是 `fixed`，而**只有
          `transform` / `filter` / `will-change` 这类祖先才会成为 `fixed` 的包含块，
          `position: absolute` 不会** —— 所以面板仍然相对**视口**定位，
          `rect.right / rect.bottom` 那套坐标计算不受影响。
          （如果这里用 `fixed`，就得把这一份触发器一起 `display:none` 掉，
          打开的瞬间「⋯」会消失 —— 那是没必要的倒退。）
          这不改变任何菜单行为：开合、点外关闭、Esc、方向键、确认弹窗全部照旧；
          宽屏（`md` 及以上）它的定位**一个字都没变**（仍是行内正常流里的 `shrink-0`）。
        */
        open && 'absolute right-2 md:static',
      )}
      /*
        ⛔ `data-no-row-toggle`：**整行可点**（见 `ROW_TOGGLE_EXCLUDE`）之后，
        这一整块必须排除在"点行展开 / 收起"之外 —— 它同时盖住两样东西：
          · 「⋯」触发器（真 `<button>`，本来就会被 `button` 那条规则排除）；
          · **浮层菜单面板**（`fixed` 定位，但 DOM 上仍是这一块的后代：菜单项之间的
            空隙、分组分隔线都不是 `button`，只靠标签选择器会漏 —— 点分隔线会把
            上面的行折叠掉，是个很难查的 bug）。
        用标记而不是 `stopPropagation`：行点击的排除逻辑只有
        `isRowToggleExcluded` 这一处（"默认参与、只列例外"），
        而这个标记就是"这一块是例外"的唯一声明。
      */
      data-no-row-toggle=""
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`更多操作：${label}`}
        className={cn(
          // ghost / 无边框：行里现在只剩它一个按钮，但也不该抢正文的视觉重量
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-lg leading-none',
          'text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700',
          'aria-expanded:bg-slate-100 aria-expanded:text-slate-700',
          /*
            ⚠️ `mt-1 md:mt-0` 是**下钻形态残留的偏移**，别再无条件加回来：
            当年窄屏下钻行的可点区域是一个整块 `<button>`，文字基线跟树形行不同，
            才需要这 4px 把窄屏的「⋯」往下挪一点与节点名对齐。
            下钻被 `DRILL_ENABLED = false` 停用、窄屏也改走树形之后，它唯一的实际效果
            就剩"窄屏树形行的「⋯」比桌面低 4px"—— 这个偏移没有存在理由了。

            所以这里把条件从"断点"改成"**形态**"：只有下钻那一份
            （`variant === 'narrow'`，由 `MenuVariantContext` 提供）保留原样，
            **树形行在两种断点下都不再受它影响**（树形行永远是 `'wide'`，
            窄屏与桌面因此落在**同一个垂直位置**，也就是原来桌面的那一档）。
            这样既统一了树形行，也没有改动那套已停用下钻代码的行为（要恢复下钻时，
            「⋯」的偏移依旧是它当年调好的样子）。
          */
          variant === 'narrow' && 'mt-1 md:mt-0',
        )}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {open && position && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={`「${label}」的操作`}
          style={{ top: position.top, left: position.left, maxWidth: position.maxWidth }}
          className={cn(
            'fixed z-50 min-w-[11rem] rounded-xl border border-slate-200 bg-white py-1 shadow-lg',
            'max-h-[70vh] overflow-y-auto',
            /*
              ⚠️ 面板是 `fixed`，**父级的 `display:none` 藏不住它**（脱离正常流）。
              而"树形 / 下钻"两份渲染都在 DOM 里，所以必须显式只在当前断点显示：
              宽屏那一份 `md:block`、窄屏那一份 `md:hidden`。
              少了这个，打开一行菜单会同时出现两个面板（一份来自树、一份来自下钻列表）。
              面板的定位、内容、键盘行为都不受影响 —— 这里只控制"哪一份可见"。
            */
            variant === 'wide' ? 'md:block' : 'md:hidden',
          )}
        >
          {children(pick)}
        </div>
      )}
    </div>
  );
}

/**
 * **节点行**的操作菜单：行内只剩一个「⋯」，所有动作（含原来的主操作）都在菜单里。
 *
 * 分组顺序（自上而下）：
 *  新建：加子项 / 加打卡任务      ← 两个条件都不成立时，**整组不显示**（不显示灰项）
 *  ──────
 *  状态：暂停 · 完成 · 停止（暂停中则是恢复 · 完成 · 停止；终态则是重新开启）
 *  ──────
 *  管理：编辑 · 归档 / 取消归档
 *  ──────
 *  危险：删除
 *
 * 能力、权限与确认弹窗和收拢之前**一个字都没改** —— 变的只有入口：
 * 点菜单项时先关菜单，再把原来的回调交回页面（确认框照旧由页面弹出）。
 */
function NodeActionsMenu({
  node,
  archived,
  open,
  canAddChild,
  canAddHabit,
  onToggle,
  onClose,
  onAddChild,
  onAddHabit,
  onPause,
  onResume,
  onComplete,
  onStop,
  onReopen,
  onEdit,
  onArchive,
  onDelete,
}: {
  node: NodeSummary;
  archived: boolean;
  open: boolean;
  /** 未归档 && 有新建权限 && 还没到三级（与原来的「加子项」按钮同一条件）。 */
  canAddChild: boolean;
  /** 是叶子 && 进行中 && 未归档（与原来的「加打卡任务」按钮同一条件）。 */
  canAddHabit: boolean;
  onToggle: () => void;
  onClose: () => void;
  onAddChild: () => void;
  onAddHabit: () => void;
  onPause: () => void;
  onResume: () => void;
  onComplete: () => void;
  onStop: () => void;
  onReopen: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  /**
   * 状态组本次是否真的渲染了项。四个状态必然命中其一，所以它等价于
   * `!archived` —— 抽成变量只是为了让"管理组前面要不要加分隔线"读起来直观，
   * 也避免以后加状态时漏掉这里。
   */
  const statusItemsShown =
    !archived &&
    (node.status === 'active' ||
      node.status === 'paused' ||
      isTerminalNodeStatus(node.status));

  return (
    <RowMenu open={open} label={node.name} onToggle={onToggle} onClose={onClose}>
      {(pick) => (
        <>
          {/*
            新建组（最上面）：原来行内那个"主操作"搬进来了，并且把两个入口都列出来。
            条件与原来的按钮完全一致；两个都不适用时整组连分隔线一起不渲染。
          */}
          {(canAddChild || canAddHabit) && (
            <>
              {canAddChild && <NodeMenuItem onSelect={pick(onAddChild)}>加子项</NodeMenuItem>}
              {canAddHabit && <NodeMenuItem onSelect={pick(onAddHabit)}>加打卡任务</NodeMenuItem>}
              <NodeMenuSeparator />
            </>
          )}

          {/*
            状态组：暂停 / 恢复、完成、停止 / 重新开启（按当前状态显示对应项）。
            已归档的节点整组不显示 —— 归档的语义就是"先收起来"，要改状态先取消归档
            （与收拢之前的行为完全一致）。
          */}
          {!archived && node.status === 'active' && (
            <>
              <NodeMenuItem onSelect={pick(onPause)}>暂停</NodeMenuItem>
              <NodeMenuItem onSelect={pick(onComplete)}>完成</NodeMenuItem>
              <NodeMenuItem onSelect={pick(onStop)}>停止</NodeMenuItem>
            </>
          )}
          {!archived && node.status === 'paused' && (
            <>
              <NodeMenuItem onSelect={pick(onResume)}>恢复</NodeMenuItem>
              <NodeMenuItem onSelect={pick(onComplete)}>完成</NodeMenuItem>
              <NodeMenuItem onSelect={pick(onStop)}>停止</NodeMenuItem>
            </>
          )}
          {!archived && isTerminalNodeStatus(node.status) && (
            <NodeMenuItem onSelect={pick(onReopen)}>重新开启</NodeMenuItem>
          )}

          {/* 管理组：编辑、归档 / 取消归档（任意状态都有） */}
          {statusItemsShown && <NodeMenuSeparator />}
          <NodeMenuItem onSelect={pick(onEdit)}>编辑</NodeMenuItem>
          <NodeMenuItem onSelect={pick(onArchive)}>{archived ? '取消归档' : '归档'}</NodeMenuItem>

          {/* 危险组：删除（红色文字） */}
          <NodeMenuSeparator />
          <NodeMenuItem danger onSelect={pick(onDelete)}>
            删除
          </NodeMenuItem>
        </>
      )}
    </RowMenu>
  );
}

/**
 * **单条打卡任务**的操作菜单：编辑 / 停用（已停用时是「启用」）/ 删除。
 *
 * ⚠️ 一个叶子节点下面可以挂**多条**打卡任务，所以每条任务各有自己的 `⋯`
 * （id 用 `h:<habit.id>`，见 `habitMenuId` 的约定）—— 绝不能用一个节点级菜单
 * 去表达多条的操作用。
 *
 * 停用会保留历史统计、删除必须先确认：这两条与从前的按钮入口**完全一致**，
 * 只是入口从行内按钮变成了菜单项（确认弹窗仍旧由页面负责弹）。
 */
function HabitActionsMenu({
  habit,
  open,
  onToggle,
  onClose,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  habit: Habit;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: () => void;
  /** 停用 / 启用（按 `habit.active` 决定文案，行为沿用原来的按钮）。 */
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  return (
    <RowMenu open={open} label={habit.name} onToggle={onToggle} onClose={onClose}>
      {(pick) => (
        <>
          <NodeMenuItem onSelect={pick(onEdit)}>编辑</NodeMenuItem>
          <NodeMenuItem onSelect={pick(onToggleActive)}>
            {habit.active ? '停用' : '启用'}
          </NodeMenuItem>

          {/* 危险组：删除（红色文字） */}
          <NodeMenuSeparator />
          <NodeMenuItem danger onSelect={pick(onDelete)}>
            删除
          </NodeMenuItem>
        </>
      )}
    </RowMenu>
  );
}

// ---------------------------------------------------------------- 形态开关（树形 / 窄屏下钻）
//
// 页面上本来有**两种形态**：宽屏（≥ md）走树形展开，窄屏（< md）走"逐层下钻"。
// 本轮把窄屏**也切到树形**看效果（用户要求："把手机上的排版也搞成这样看看效果"），
// 但下钻的**代码一行都没删** —— 它只是被这一个开关暂时挡在渲染之外。
//
// ⚠️⚠️ **如何恢复下钻**：把下面 `DRILL_ENABLED` 从 `false` 改成 `true`（**一行**）。
//   改回去之后，两处自动同时复位，不需要再动其它任何地方：
//     · `TREE_SHOWN` 会变回 `hidden md:block` —— 树形只在 md 及以上显示；
//     · 页面里 `{DRILL_ENABLED && (…)}` 那一段下钻 UI 会重新进入 DOM（它自己的
//       `md:hidden` 一个字没动）。
//   ⛔ 不要用"删掉下钻那一段"来达成"窄屏也用树形"：那会让恢复变成一次大改。
const DRILL_ENABLED = false;

/**
 * **树形**那一套渲染的显示类 —— **唯一**一处开关（页面里所有属于树形的容器都从这里取）。
 *
 * 为什么必须是常量而不是在各处手写 `hidden md:block`：树形的 DOM 是**一层套一层**的
 * （外层卡片 → 每一行 → 子树 → 打卡任务区 → 收起提示），只要漏掉其中一层的 `hidden`，
 * 窄屏下就会看到"卡片在、里面的行全没了"这种半截状态。
 * 收成一处之后，"树形在哪些断点显示"只有这一个答案。
 *
 * ⚠️ `md:block` 在两种取值下都保留是有意的：它让"恢复下钻"只需改 `DRILL_ENABLED`，
 *   而不必回来重排这些类名 —— `DRILL_ENABLED` 为 `true` 时这里取到的就是改动前
 *   逐字相同的 `'hidden md:block'`。
 * ⚠️ `'hidden md:block'` / `'hidden'` / `'block'` 都是**源码里的字面量**，
 *   Tailwind 的扫描器看得见它们，所以三种类名都会真的进 CSS（不靠运行时拼字符串）。
 */
const TREE_SHOWN = DRILL_ENABLED ? 'hidden md:block' : 'block';

// ---------------------------------------------------------------- 树

interface TreeContext {
  actor: Actor;
  canCreateHere: boolean;
  onAddChild: (node: NodeSummary) => void;
  onEdit: (node: NodeSummary) => void;
  /**
   * 状态变更入口。**每个动作都单独一个回调**（而不是传一个 action 参数），
   * 因为暂停 / 停止 / 恢复 / 重新开启都必须先弹确认、且文案各不相同。
   */
  onPause: (node: NodeSummary) => void;
  onResume: (node: NodeSummary) => void;
  onComplete: (node: NodeSummary) => void;
  onStop: (node: NodeSummary) => void;
  onReopen: (node: NodeSummary) => void;
  /** 归档 / 取消归档（按 `node.archivedAt` 决定文案与目标状态）。 */
  onArchive: (node: NodeSummary) => void;
  onDelete: (node: NodeSummary) => void;
  /** 给**叶子节点**加一条打卡任务（非叶子不显示入口，数据库触发器也会拒绝）。 */
  onAddHabit: (node: NodeSummary) => void;
  /** 编辑已挂在该节点上的打卡任务。 */
  onEditHabit: (node: NodeSummary, habit: Habit) => void;
  /** 停用 / 启用打卡任务（停用保留历史统计）。 */
  onToggleHabit: (habit: Habit) => void;
  /** 删除打卡任务（必须先经确认）。 */
  onDeleteHabit: (habit: Habit) => void;
  /** 展开的节点集合（受控，避免每次重渲染都塌回去）。 */
  expanded: Set<string>;
  toggle: (id: string) => void;
  /**
   * 当前打开的那个「⋯」菜单的 **id**（`null` = 都关着）。
   *
   * ⚠️ 刻意是**一个共享的单一状态**（`string | null`），而不是每个行各自的
   * boolean —— 后者会让多个菜单同时展开，屏幕上挂着一堆浮层。
   * 打开时写入本行的 id 即可自动把上一个关掉。
   *
   * 而且它**同时管两类菜单**：节点行（`n:<nodeId>`）与每条打卡任务行
   * （`h:<habitId>`）—— 所以"打开打卡任务的菜单会把节点的菜单关掉"、
   * "打开节点的菜单会把打卡任务的菜单关掉"这两件事是同一个状态自然带来的，
   * 不需要额外的联动代码。id 前缀的约定见 `nodeMenuId` / `habitMenuId`。
   */
  openMenuId: string | null;
  menuOpenAt: (id: string) => void;
  menuClose: () => void;
}

/**
 * **节点行的信息区**（左侧色条之后的全部文字 + 徽章 + 进度）。
 *
 * 宽屏（树形）与窄屏（逐层下钻）两种形态共用这一段 —— 不是各写一份，
 * 所以"**层级序号圆标** / 名称 / 状态文字 / 归档标记 / 归属标注 / 第 N 级 / 起止日期 /
 * 进度 / 未结束子目标数"在两个形态里天然一模一样，以后改文案也不会只改一边。
 *
 * ⚠️ 序号圆标是**固定 24px 的正圆 + `shrink-0`**，标题块在它旁边 `min-w-0` 收缩：
 * 窄屏上它**绝不会被压扁**（压扁的 `.rounded-full` 会变成胶囊），代价是节点名可用宽度
 * 少了 32px（24px 圆 + `gap-2` 的 8px）。这就是"窄屏略显拥挤"的唯一来源，
 * 换来的是"不点进去也知道自己在第几级"；圆本身很小，实测（按 360px 视口推算）
 * 标题仍有两百多像素可换行，不会出现"一个字一行"。
 *
 * 两侧的差异**刻意留在调用处**，不塞进这里（窄屏的"进入"提示留在 `DrillRow`，
 * 宽屏的折叠状态指示由 `collapse` 插槽从 `NodeRow` 传进来）——
 * 这样"两个形态各自不同的那一点点"始终看得见，不会藏进共用组件里慢慢跑偏。
 *
 * ⚠️ `collapse` 是**插槽**而不是内部逻辑：折叠状态指示的位置必须落在**「第 N 级」文字之后**，
 * 而那段文字就在这里渲染 —— 所以插槽放在这里；但**渲染与否**仍由调用处决定
 * （窄屏下钻**不传**，所以下钻 DOM 里根本没有这个指示；它本身也不再是可交互控件，
 * 展开 / 收起由整行负责，见 `NodeRow`）。
 */
function NodeHeaderContent({
  node,
  archived,
  collapse,
}: {
  node: NodeSummary;
  archived: boolean;
  /**
   * 折叠**状态指示**（`▸/▾`）的插槽，渲染在「第 N 级」**之后**。
   *
   * ⚠️ 它现在只是**纯视觉元素**（`aria-hidden`、不是按钮、没有 `onClick`）：
   * 展开 / 收起由**整行**这个控件负责（见 `NodeRow` 行容器上的
   * `role="button"` / `onClick` / `onKeyDown` / `aria-expanded`），
   * 这里只负责"画出当前是开着还是合着"。
   *
   * 为什么不放在行首：行首多一个元素，就会让"有子项的行"比"没有子项的行"多占
   * 一个元素的宽度，**同一层级的行左边缘因此对不齐**（序号圆标的位置会错开）。
   * 放到「第 N 级」后面之后，每一行的**第一个元素都是序号圆标**，天然对齐；
   * 没有子项的行也不需要留占位（插槽为空 = 不渲染任何东西）。
   */
  collapse?: ReactNode;
}) {
  const meta = STATUS_META[node.status];
  /**
   * 子项里"还没结束"的数量 —— **只数直接下级**（2026-10-30 用户要求）。
   *
   * > 用户原话："下面每个'目标'只显示其直接下级目标的数量，不是显示下面所有目标的数量。"
   *
   * 旧写法用 `countDescendants()`（递归整棵子树），所以"数学"会显示成 2 个，
   * 而它其实只有 2 个直接子目标、再往下还有一层 —— 数字与界面上能点开的层数对不上。
   * 现在与"展开这一行能看到几行"完全一致。
   */
  const openChildren = (node.children ?? []).filter(
    (child) => !isTerminalNodeStatus(child.status),
  ).length;
  /**
   * 标题字级与**层级序号圆标**（都从 `levelStyle` 取，层级只有一处定义）。
   *
   * `title` ⚠️ **三层是同一个值**（标题不承担层级，见 `LEVEL_TITLE`），而且
   * **只加在标题这一个 `<p>` 上**：同一行还有状态文字 / 徽章 / 第 N 级 / 起止日期 /
   * 进度条，那些是**另一套语义**（次级信息），它们的 `text-xs` / `text-slate-400`
   * 等既有样式一个字都不受影响。
   *
   * `badge` 是标题**前面**那个正圆序号（1 / 2 / 3）：
   *  - 固定 `h-6 w-6` 正圆 + `text-xs` + `leading-none`：数字恒定居中，不随标题字号变化；
   *  - `shrink-0`：窄屏一行里它**绝不参与压缩**，宁可让标题换行也不会把圆挤成椭圆
   *    （`.rounded-full` 遇到被压扁的盒子会变成胶囊，那就不再是"圆底色"了）；
   *  - `aria-hidden="true"`：它是**纯装饰**——层级信息在同一条行的「第 N 级」文字里已经有了，
   *    读屏再播一次"1"只会变成噪音。
   */
  const level = levelStyle(node.depth);
  const title = level.title;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/*
          2026-10-30 用户要求："尝试把各级目标前面的目标级别数字去掉或隐藏，我想看一下效果"。
          所以**行首那个正圆序号（1/2/3）不再渲染**（层级仍能靠缩进 + 行底色看出来，
          文字版「第 N 级」还留在标题后面）。

          ⚠️ 原来的注释（留档）：它是所有行的第一个元素，固定 `h-6 w-6` 正圆 + `shrink-0`，
          保证同一层级左边缘天然对齐。去掉之后标题直接贴左边，对齐由缩进负责。
        */}
        <p className={title}>{node.name}</p>
        {/*
          状态**只用文字**表达，颜色取自 `STATUS_ACCENT` —— 与左侧那根色条同源
          （进行中在这里同样是**中性灰**，见 STATUS_ACCENT 的注释）。
          刻意不用 `<Badge tone=…>`：Badge 的 tone 是本页另一套颜色表
          （active=success 绿 / completed=brand 蓝），与色条的
          （active=不画条 / completed=emerald 绿）**正好相反** ——
          同一个状态在同一行出现两种颜色，看起来就像 bug。
          状态在页面上出现两次（色条 + 文字）已经足够；再加一层带色边框的徽章
          纯属噪音（用户反馈过"眼花缭乱"）。所以这里保留文字、去掉徽章外壳。
        */}
        <span className={cn('text-xs font-medium', STATUS_ACCENT[node.status].text)}>
          {meta.label}
        </span>
        {/*
          归档独立成一个小标记，**不混进状态标签** ——
          "已归档的已完成目标"必须能同时看到这两件事。
        */}
        {archived && <Badge tone="neutral">已归档</Badge>}
        {/* 2026-10-30 用户要求去掉「家长创建 / 学生创建」这枚徽章（行上其余信息保留）。 */}
        <span className="text-xs text-slate-400">第 {node.depth} 级</span>
        {/*
          折叠状态指示插槽：**在「第 N 级」之后**，不在行首 —— 见 `collapse` 的说明。
          窄屏下钻不传这个插槽，所以下钻不会出现任何多余的按钮。
        */}
        {collapse}
      </div>

      <p className="mt-1 text-xs text-slate-500">
        {formatDateRange(node.startDate, node.endDate, node.isLongTerm)}
        {node.note ? ` · ${node.note}` : ''}
      </p>

      {/* 2026-10-30 用户要求去掉进度条与进度数字（`0/540 · 0%` 那一条）。 */}

      {openChildren > 0 && (
        <p className="mt-1 text-xs text-slate-400">其下有 {openChildren} 个未结束的子目标</p>
      )}
    </>
  );
}

/**
 * **叶子节点下面那几条打卡任务**（每条一个 `⋯`）。
 *
 * 宽屏与窄屏共用：每条任务的图标 / 名称 / 频率 / 目标值 / 积分 / 已停用标记，
 * 以及每条自己的 `⋯` 与它的 id 约定（`h:<habitId>`），两处必须完全一致 ——
 * 所以这里只实现一次。**外层容器与"还没有打卡任务"的空态留在调用处**：
 * 宽屏是一个缩进的子区域，窄屏要"就地"展开（无缩进），两边的容器不同。
 */
function NodeHabitLines({
  node,
  ctx,
  canEdit,
}: {
  node: NodeSummary;
  ctx: TreeContext;
  canEdit: boolean;
}) {
  return (
    <ul className="space-y-1">
      {(node.habits ?? []).map((habit) => (
        <li
          key={habit.id}
          className={cn(
            /*
              打卡任务子行：底色取 `HABIT_ROW_BG`（**唯一来源**，不随父级层级变化）。
              它依然是"子行"，但**靠结构表达、不靠底色**：左侧一根**更细的**引导线
              （1px，节点层级之间用的是 2px）+ 缩进（调用处给的 `ml-6` / `pl-1`）
              + 更小的字号（`HABIT_TEXT`）——
              这样"它是这个节点的孩子"一眼可见，又不会比节点还抢眼。
              ⛔ 不要再按 `node.depth` 换底色：那正是"2 级下面的打卡任务与 3 级下面的
              长得不一样"这个 bug 的成因（同一个概念，身份不该由父级决定）。
              ⚠️ 这里**不区分宽屏 / 窄屏**：两种形态的节点行用的是同一张 `LEVELS` 表，
              子行的底色当然也只能是同一个值。
            */
            'flex items-center gap-2 rounded-lg border-l border-slate-200 px-2 py-1',
            HABIT_TEXT,
            HABIT_ROW_BG,
          )}
        >
          {/*
            左侧信息保持原样（图标 / 名称 / 频率 / 目标值 / 积分 / 已停用）。
            `min-w-0` 与节点行同理：少了它，右侧的「⋯」触发器
            （`shrink-0`，自己不收缩）就会把这块文字挤成"一个字一行"。
          */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {/*
              循环/重复语义用 `🔁` emoji。
              这里曾经为"统一图标风格"换成过手写内联 SVG（`HabitRepeatIcon`），
              但用户明确说那个不好看、要换回 emoji —— 所以恢复。
              经验：**统一图标风格**这件事，用户抱怨的是"目标用箭靶、任务用书"那种
              **按 kind 随机选 emoji**；而 `🔁` 本身既没被抱怨过、语义也直白，
              不需要为了"风格一致"去动它。不要拿设计原则去改用户没提意见的东西。
            */}
            <span aria-hidden="true" className="text-sm leading-none">
              🔁
            </span>
            {/* 行内主次（不是层级梯度）：这条任务的名称保持自己的字重，灰色附属信息不动 */}
            <span className="font-medium">{habit.name}</span>
            <span className="text-slate-400">{describeRecurrence(habit)}</span>
            {habit.targetValue !== null && (
              <span className="text-slate-400">
                目标 {habit.targetValue}
                {habit.unit ?? ''}
              </span>
            )}
            <span className="text-amber-600">
              {describePointsRule({
                pointsPerUnit: habit.pointsPerUnit,
                unitSize: habit.pointsUnitSize,
                targetValue: habit.targetValue,
                unit: habit.unit,
                dailyPointsCap: habit.dailyPointsCap,
              })}
            </span>
            {!habit.active && <Badge tone="neutral">已停用</Badge>}
          </div>
          {/*
            每条打卡任务各有自己的菜单（一个叶子可以挂多条，用节点级菜单
            表达不了）；权限沿用该节点的 canEdit 判定，无权限就整块不渲染。
          */}
          {canEdit && (
            <HabitActionsMenu
              habit={habit}
              open={ctx.openMenuId === habitMenuId(habit.id)}
              onToggle={() =>
                ctx.openMenuId === habitMenuId(habit.id)
                  ? ctx.menuClose()
                  : ctx.menuOpenAt(habitMenuId(habit.id))
              }
              onClose={ctx.menuClose}
              onEdit={() => ctx.onEditHabit(node, habit)}
              onToggleActive={() => ctx.onToggleHabit(habit)}
              onDelete={() => ctx.onDeleteHabit(habit)}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- 整行可点（展开 / 收起）
//
// 用户反馈："点 ▸ 来展开和收起，还是不太方便，很小不好找，点也不好点，尤其在手机。
// 能不能改成直接点框（除了功能按钮和链接）？"
//
// 于是**节点行整行**成为展开 / 收起的点击区域（只有"有子项"的行可点，叶子行什么都不做）。
//
// ⛔⛔ **排除逻辑只用"事件源判定"这一种写法，不要在每个子元素上写 `stopPropagation`**：
//   · `stopPropagation` 是"例外散落在每个元素自己身上"：每加一个新元素（以后往行里放一个
//     链接、一个输入框、一个开关）都必须记得回去补一次，漏了就是"点它顺带把行折叠了"，
//     而且**没有任何一处能一眼看出漏了**；
//   · 事件源判定是"**默认全部参与、只列出例外**"：例外只有下面这一个选择器，
//     位置固定、一眼能核对完（标签类选择器还顺手覆盖了所有以后新增的真控件）。
//
// 例外分两类，都写进同一个选择器：
//   1. **结构性交互元素**：`button / a / input / select / textarea / label`
//      —— 行内以后新增的任何真控件都会被自动排除（不需要靠调用方记得加标记）；
//   2. **显式标记 `data-no-row-toggle`**：给那些"不是 button 但也绝不能触发展开"的
//      区域用。目前只有一处 —— `RowMenu` 的外壳（它同时盖住「⋯」触发器和
//      **浮层菜单面板**：面板是 `fixed` 定位，但 DOM 上仍然是行内的后代，
//      菜单项之间的空隙、分组分隔线都不是 `button`，只靠第 1 类会漏）。
//      `[role="menu"]` 一起列进来是第二道保险（面板本身也带这个 role）。
//
// ⚠️ 注意选择器里**没有** `[role="button"]`：行自己就是 `role="button"`，
//   把它写进去会让 `closest()` 在行自身上就命中，整行永远点不动。
const ROW_TOGGLE_EXCLUDE =
  'button, a, input, select, textarea, label, [role="menu"], [data-no-row-toggle]';

/**
 * 这次点击 / 按键是否**不应该**触发展开收起（点在（或来自）例外元素上）。
 *
 * 拿不到元素时返回 `true`（保守：宁可不切换，也不要误切换）。
 */
function isRowToggleExcluded(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  return target.closest(ROW_TOGGLE_EXCLUDE) !== null;
}

/**
 * 一行节点（含它的打卡任务区与全部子树）。
 *
 * **整行可点**：有子项的行本身就是一个控件（`role="button"` + `tabIndex` +
 * `aria-expanded` + Enter/Space），点行内任意位置切换展开 / 收起；叶子行不参与
 * （既没有 `role` / `tabIndex`，也没有 `cursor-pointer` 和 hover）。
 * 判据 / 排除规则见上面的 `ROW_TOGGLE_EXCLUDE`。
 *
 * `className` 只给**顶级那几行**用：页面在最外层 `<ul>` 上把"每棵子树"拉开距离、
 * 并给第 2 棵起加一条很轻的分割线（见页面里的 `<ul>`）。子行递归时不传，
 * 所以树内部的行距仍然是紧凑的 `space-y-1`。
 */
function NodeRow({
  node,
  ctx,
  className,
}: {
  node: NodeSummary;
  ctx: TreeContext;
  className?: string;
}) {
  // 只有叶子节点能挂打卡任务（契约 1.2：非叶子禁用入口并给出说明）
  const isLeaf = (node.children?.length ?? 0) === 0;
  const canEdit = canEditContent(ctx.actor, ownershipOf(node));
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = ctx.expanded.has(node.id);
  const archived = isArchived(node);
  /**
   * 打卡任务区只在**进行中**的节点下显示。
   *
   * 口径与后端一致："只有进行中的目标才把孩子端打卡显示出来"（暂停 / 完成 / 停止
   * 都不该再出现新打卡）。旧代码写的是"非 archived 就显示" —— 那种写法在新模型下
   * 会把暂停、停止的目标也显示成可以打卡，与后端行为对不上。
   */
  const showCheckins = node.status === 'active' && !archived;

  /**
   * 菜单里「新建」组两个入口的可用条件 —— 与收拢前那个行内主操作按钮**完全一致**：
   *  - 加子项：未归档 && 有新建权限 && 还没到三级；
   *  - 加打卡任务：是叶子 && 进行中（`showCheckins`）&& 未归档。
   * 两个都不成立时整组不渲染（见 `NodeActionsMenu`），行内就只剩「⋯」。
   *
   * 注意：一个"还没长出子项的浅层节点"两个条件会同时成立 —— 以前只显示
   * 「加子项」，现在菜单里两条都列出来，这正是本轮要的效果（入口集中、不遗漏）。
   */
  const canAddChild = !archived && ctx.canCreateHere && node.depth < MAX_NODE_DEPTH;
  const canAddHabit = isLeaf && showCheckins;
  /** 该层级的整套样式（行底色，见文件上方的 LEVELS）。 */
  const level = levelStyle(node.depth);

  /**
   * **只有"有子项"的行才是控件**。叶子行可点也换不来任何东西（没有子项可展开），
   * 所以它**一个交互属性都不加**：没有 `role` / `tabIndex` / `onClick` / `onKeyDown`，
   * 也没有 `cursor-pointer` 与 hover —— 不然读屏会把它播成按钮、鼠标也会以为它能点，
   * 而点下去什么都不会发生（用户原话要求："叶子行点了不做任何事"）。
   */
  const isRowControl = hasChildren;

  /**
   * 整行点击 = 展开 / 收起。
   *
   * ⚠️ 用 `onClick` 而**不是** `pointerdown` / `touchstart`：`click` 只在"按下与抬起
   * 落在同一个元素上"时才触发，所以手机**滑动页面时经过行不会误触**（滑动会被
   * 浏览器判定成滚动，click 不触发）。`pointerdown` 则是一碰就切换 —— 这是本行
   * 刻意避开的写法。
   *
   * ⚠️ 下面每个 handler 里的 `if (!isRowControl) return;` 是**防御性**的：
   * 行容器上挂不挂这两个 handler 本身也由 `isRowControl` 决定（叶子行压根不挂），
   * 留着这道判断是为了"以后有人把 handler 改成无条件挂上"时它仍然不会误切换。
   */
  const handleRowClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isRowControl) return;
    // 例外元素（「⋯」、浮层菜单、行内以后新增的任何控件）不触发展开收起，见上面的选择器
    if (isRowToggleExcluded(event.target)) return;
    ctx.toggle(node.id);
  };

  /**
   * 键盘：Enter / Space 等价于点击（行是 `role="button"` 的 `div`，
   * **浏览器不会**像真 `<button>` 那样替我们合成 click，所以这里必须自己处理）。
   *
   * ⚠️ `event.target !== event.currentTarget` 时立刻返回 —— 焦点在行内的「⋯」
   * （真 `<button>`）上时，Enter / Space 由那个按钮自己处理（开合菜单），
   * 不能顺手把整行也切换了。这就是"键盘上点「⋯」不会同时展开"的保证。
   *
   * ⚠️ Space 必须 `preventDefault()`：否则浏览器会把它当成"向下翻页"，
   * 键盘用户按一下展开、页面同时滚走。
   */
  const handleRowKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!isRowControl) return;
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    ctx.toggle(node.id);
  };

  return (
    <li className={className}>
      {/*
        从属关系：**引导线不再挂在每一行上，而是挂在"子区域容器"（下面的 children `<ul>`）上**
        —— 挂在行上时，兄弟行之间的 `space-y-1` 会把线切成一段一段（4px 断口），
        断线恰好破坏了"这几行是同一支"的分组感（用户原话：分不清谁是谁）。

        现在这一层只剩"已归档整枝降透明度"；缩进与引导线都由父级的 children `<ul>` 提供，
        每一级依旧是 8px（手机）/ 12px（桌面），与改动前**逐级对齐量完全一致**。
      */}
      {/*
        已归档整枝降透明度（归档只是"收起来"，不改变进展状态，所以压暗即可）。
        ⚠️ 可见性取 `TREE_SHOWN`：这里从前写死 `hidden md:block`（整行**只在宽屏渲染**），
        本轮换成常量之后，"树形在哪些断点显示"就由那一个开关统一决定 —— 见它上方的注释。
        窄屏的对应实现是 `DrillRow`（同一个 `<li>` 结构、同一批共用子组件），
        目前被 `DRILL_ENABLED` 挡在渲染之外。
      */}
      <div className={cn(TREE_SHOWN, archived && 'opacity-70')}>
        {/* 行容器：底色表达层级（标题字级三层一致，同出于 LEVELS / LEVEL_TITLE）、
            左侧色条（仅非进行中）表达状态的例外，两个手段互不代劳 */}
        {/*
          `relative` 是**必需**的，不是装饰：RowMenu 的「⋯」触发器在打开时会切成
          `absolute right-2`（那是给下钻行写的定位方式）。行容器若没有定位上下文，
          `absolute` 会相对**初始包含块**定位 —— 表现为"点开菜单时按钮往右跳出卡片"，
          同时它脱离文档流、让标题块临时变宽。给它一个 `relative` 包含块即可。
          桌面不受影响（md 时触发器回到 static，菜单面板本身是 fixed）。
          窄屏改用树形之后这个条件才成立，所以是本轮必须一起补的。

          ⛔⛔ **行本身就是"展开 / 收起"控件**（用户要求"直接点框"）：
            · `onClick`  = 整行可点（排除规则见 `ROW_TOGGLE_EXCLUDE`）——
              它只挂在这个行容器上：打卡任务区、收起提示、子树 `<ul>` 都是它的
              **兄弟**，结构上就不在可点区域里，所以**不需要** `stopPropagation`
              去拦它们（子树里的子行也各自有自己的行容器，点子行只会切换子行，
              不会顺着冒泡把父行一起折叠）；
            · `role="button"` + `tabIndex={0}` + `onKeyDown` + `aria-expanded`
              = 键盘 / 读屏可用，且读屏能播报"已展开 / 已收起"；
            · `cursor-pointer` + `transition-colors hover:bg-slate-900/5`
              = "这里能点"的视觉暗示。hover 用**与底色无关的中性叠加**，
              换层级底色时不必重挑（理由见 `DrillRow` 的同名注释）。
          ⚠️ 这三组**只给有子项的行**（`isRowControl`）：叶子行保持普通 `div`，
            没有 `role` / `tabIndex` / hover / `cursor-pointer`。
          ⚠️ 行内嵌着真的 `<button>`（「⋯」）—— `role="button"` 里套 `button` 是无障碍
            上已知的瑕疵（嵌套交互元素），但功能上两者**不会互相触发**：点「⋯」时
            `isRowToggleExcluded` 直接返回、`aria-expanded` 的切换也不会被"抢"。
            折叠指示 `▸/▾` 则已经**降级为纯视觉元素**（不是按钮、`aria-hidden`），
            所以读屏只会看到一个控件、键盘也只有一个 Tab 停留点。
        */}
        <div
          role={isRowControl ? 'button' : undefined}
          tabIndex={isRowControl ? 0 : undefined}
          aria-expanded={isRowControl ? isExpanded : undefined}
          onClick={isRowControl ? handleRowClick : undefined}
          onKeyDown={isRowControl ? handleRowKeyDown : undefined}
          className={cn(
            'relative flex items-start gap-2 rounded-lg px-2 py-2',
            level.row,
            // 可点的暗示只给可点的行；`py-2` 的行高本来就够手指点（手机整行可点）
            //
            // ⚠️ **刻意不加 hover 底色**：整行可点之后，鼠标划过就变一块底色在长列表里
            // 很晃眼（用户明确要求"悬停时底色不要变化"）。可点性由**手型光标**表达即可 ——
            // 光标本身就是"这里能点"的标准信号，不需要再动背景色。
            // 顺带也避免了那个反复出现的老问题：换层级底色就得重挑一次 hover 色。
            isRowControl && 'cursor-pointer',
          )}
        >
          {/*
            左侧 4px 状态色条（颜色映射只在 statusBarClass / STATUS_ACCENT 一处）。
            进行中时它是**透明占位**：看得见的只有 pause/completed/stopped 三种例外。
          */}
          <span aria-hidden="true" className={statusBarClass(node.status, archived)} />

          {/*
            文字块：`min-w-0` 与 `flex-1` 都不能省。
            flex 子项的 `min-width` 默认是 `auto`（≈ 内容的 min-content 宽度，中文恰好
            就是"一个字"）：操作区（触发器带 `shrink-0`，不参与压缩）要多少宽度，
            文字块就会被压到 min-content —— 也就是"一个字一行"的竖排。
            给 `min-w-0` 它才能按可用宽度正常收缩换行。⚠️ 以后若往操作区再加元素，
            记得让它能换到下一行（`flex-wrap` + 窄屏 `w-full`），而不是继续挤这一块。
          */}
          <div className="min-w-0 flex-1">
            {/*
              折叠**状态指示** `▸/▾`：由这里传进 `NodeHeaderContent`，渲染在
              「第 N 级」文字之后（也就是本行的右侧）。
              ⛔ 它**不再放在行首**：行首多一个按钮，就会让"有子项的行"比"没有子项的行"
              多占一个按钮的宽度，**同一层级的行左边缘因此对不齐**（序号圆标的位置会错开，
              用户原话："放在前面会导致同一级目标左边对不齐"）。
              移到「第 N 级」之后，所有行的第一个元素都是序号圆标，天然对齐；
              没有子项的行**不占位**（`collapse` 为 `undefined`，什么都不渲染）。

              ⛔⛔ **本轮它从 `<button>` 降级成纯视觉元素**（用户要求"整行可点"）：
                · 不再是 `<button>`、不再有 `onClick` / `aria-expanded` / `aria-label`
                  —— 展开 / 收起现在由**整行**统一处理（见行容器上的
                  `role="button"` / `aria-expanded` / `onClick`）；
                · `aria-hidden="true"`：它只是一个方向符号，读屏已经能从行的
                  `aria-expanded` 知道状态，再念一遍就是噪音；
                · 因此**读屏只会看到一个"展开"控件、键盘也只有一个 Tab 停留点**
                  （旧写法是"行 + ▸ 按钮"两个，键盘要 Tab 两次）。
              ⚠️ 它仍然留在行内，所以**点它所在的位置 = 点在行上**，
                由行容器统一处理、只触发一次切换 —— 不会出现"点 ▸ 切换两下"。
              `h-7 w-7` + `-my-1`：保持原来的尺寸与对齐（不撑高这一行、
              与"第 N 级"文字居中对齐）；hover 交给整行，这里不再自己变色。
              ⚠️ 窄屏下钻用的是另一个组件（`DrillRow`），它**根本不传这个插槽** ——
              所以下钻 DOM 里既没有 `▸/▾`，也不会多出任何按钮。
            */}
            <NodeHeaderContent
              node={node}
              archived={archived}
              collapse={
                hasChildren ? (
                  <span
                    aria-hidden="true"
                    className="-my-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm leading-none text-slate-500"
                  >
                    {isExpanded ? '▾' : '▸'}
                  </span>
                ) : undefined
              }
            />
          </div>

          {/*
            操作区：权限不足时**整块不渲染**（前端隐藏，服务端才是防线）。
            行内**只有**一个「⋯」：这里的按钮此前最多有 8 个，手机上要换两三排，
            "眼花缭乱"（用户原话）。现在连"加子项 / 加打卡任务"也进了菜单，
            能力不变、条件不变、确认弹窗不变（见 NodeActionsMenu）。
          */}
          {canEdit && (
            <NodeActionsMenu
              node={node}
              archived={archived}
              open={ctx.openMenuId === nodeMenuId(node.id)}
              canAddChild={canAddChild}
              canAddHabit={canAddHabit}
              onToggle={() =>
                ctx.openMenuId === nodeMenuId(node.id)
                  ? ctx.menuClose()
                  : ctx.menuOpenAt(nodeMenuId(node.id))
              }
              onClose={ctx.menuClose}
              // 回调体内**不关菜单**是有意的：菜单项自己的 pick 已经先关菜单、
              // 再调这些回调（见 RowMenu 的 pick）。这样"原有的确认弹窗
              // 照常弹出"这条行为一个字都不用改。
              onAddChild={() => ctx.onAddChild(node)}
              onAddHabit={() => ctx.onAddHabit(node)}
              onPause={() => ctx.onPause(node)}
              onResume={() => ctx.onResume(node)}
              onComplete={() => ctx.onComplete(node)}
              onStop={() => ctx.onStop(node)}
              onReopen={() => ctx.onReopen(node)}
              onEdit={() => ctx.onEdit(node)}
              onArchive={() => ctx.onArchive(node)}
              onDelete={() => ctx.onDelete(node)}
            />
          )}
        </div>
        {/* ↑ 行容器结束：下面这些都是"这一行的孩子"，所以落在行的底色之外 */}

        {/*
          叶子节点：显示挂着的打卡任务，每条任务一个「⋯」（编辑 / 停用 / 删除）。
          ⚠️ 可见性取 `TREE_SHOWN`：从前这里写死 `hidden md:block`（"这是桌面树形的
          打卡任务区"）。本轮窄屏也用树形，于是它跟着树形一起显示；下钻形态下叶子
          的打卡任务是**就地**显示在下钻行里的（见 `DrillRow`，复用同一个
          `NodeHabitLines`），两种形态**只会渲染一份**，不会重复。
        */}
        {isLeaf && showCheckins && (
          /*
            打卡任务区：底色取 `HABIT_ROW_BG`（**唯一来源**，不随父级层级变化）、
            引导线**更细**（每条任务 1px，节点层级之间是 2px），这两条保持原样。
            对齐在"连续引导线"的新结构下没有变化：节点的缩进量由父级 children `<ul>`
            提供（`border-l-2 + pl-2 sm:pl-3`），与改动前挂在这一层 `<div>` 上时
            **逐像素一致**，所以这里 `ml-6 sm:ml-7` 的偏移量继续有效。
          */
          <div className={cn('mt-1 ml-6 sm:ml-7', TREE_SHOWN)}>
            {(node.habits ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">
                {canEdit
                  ? '还没有打卡任务 —— 打开上面这一行的「⋯」，选「加打卡任务」，给它加一条「每天做什么」。'
                  : '还没有打卡任务。'}
              </p>
            ) : (
              <NodeHabitLines node={node} ctx={ctx} canEdit={canEdit} />
            )}
          </div>
        )}

        {/*
          收起提示：明确告诉家长"这里还有东西，可以展开"。
          ⚠️ 可见性取 `TREE_SHOWN`：它属于树形（折叠是树形独有的动作）。
          本轮窄屏也用树形，所以窄屏**也会**看到这条提示，`▸` 也在（位置在「第 N 级」
          之后）。默认仍然是全部收起，没有任何"自动展开"。
          ⚠️ 位置词本轮**已按实际位置改正**：折叠开关早就从行首挪到了「第 N 级」之后
          （也就是同一行的**右侧**），而文案里还写着"点左侧 ▸"—— 指错了方向。
          现在写的是"点右侧 ▸"。（顺带说明：能展开的地方已经不只是那个 `▸`
          —— **整行都可点**，见 `NodeRow` 的行点击逻辑；这里保留 `▸` 是为了把"点哪里"
          说得具体，而不是因为只有它能点。）
          ⛔ 全页面只有这一处位置词会指错方向；文件里其余出现"左侧"的地方
          （状态色条 / 引导线 / 打卡任务行）说的都是真的在左边的东西，不要一起改。
        */}
        {/*
          2026-10-30 用户要求去掉「已收起 N 个子项 —— 点右侧 ▸ 展开」这一行：
          整行本来就可点（`NodeRow` 的行点击逻辑），右侧还有 `▸/▾` 指示，这行文字是冗余的。
        */}

        {/*
          ⚠️ 可见性取 `TREE_SHOWN`：子树属于树形。下钻形态下一次只显示一层，
          它自己渲染 `visibleNodes` 的下一层（见页面里那段下钻 `<ul>`）——
          两种形态**只渲染一份**，不会重复。
        */}
        {hasChildren && isExpanded && (
          /*
            「百折页」的分组感靠这两件事表达，**不加任何新按钮**：
             1. 子区域上方一条极轻的 `border-t border-slate-100`（父行与子区域的分界）；
             2. **引导线挂在这个 `<ul>` 上**（不是挂在每一行上）：于是它从父行下方
                一路贯穿到最后一个子项，兄弟行之间的 `space-y-1` 间隙也落在这条线里面
                —— 线是**连续**的，不再有 4px 断口。缩进（`pl-2 sm:pl-3`）与线放在同一处，
                "缩进跟着线走"的关系一眼可见。
            子行自己的层级底色取自同一张 `LEVELS`（`row`），层级不靠线来表达。
          */
          <ul
            className={cn(
              'mt-1 space-y-1 border-l-2 border-l-slate-200 border-t border-t-slate-100 pt-1 pl-2 sm:pl-3',
              TREE_SHOWN,
            )}
          >
            {(node.children ?? []).map((child) => (
              <NodeRow key={child.id} node={child} ctx={ctx} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- 窄屏：逐层下钻
//
// ⚠️⚠️ **这一套现在被 `DRILL_ENABLED = false` 挡在渲染之外（暂时停用，代码一行没删）**。
// 用户此前在手机上否掉过树形，理由是"全是灰的 + 每行最多 8 个按钮"；这两条现在都消除了
// （彩色分级 + 序号圆标 + 每行只剩一个「⋯」），所以本轮让窄屏也走树形看效果。
// 要恢复下钻：把 `DRILL_ENABLED` 改成 `true`（一行），见那个常量上方的注释。
//
// 为什么当初改结构而不是继续调样式（三轮配色 / 图标 / 引导线的微调都失败后的结论）：
// 窄屏上"用缩进表达层级"根本行不通 —— 宽度不够，层级一深，满屏都是竖条与缩进，
// 一眼看去分不清谁是谁（用户原话："还是分不清！"）。
//
// 所以窄屏**不再用缩进表达从属关系**，而是**一次只显示一层**：点一个有子项的节点就"进入"它，
// 看到它的子项；顶部面包屑逐级回退。宽屏（≥ md）保持原有树形展开，一行都没改。
// （层级本身在窄屏依旧看得见，而且**比缩进时代更明确**：每行自己的底色（每级一个明亮色相）
//  与**标题前面的层级序号圆标**都按 depth 取值，见 LEVELS —— 下钻时看不见全貌，
//  "我现在在第几级"这件事就由那个圆标直接说出来。标题字级**三层一致**，
//  不参与层级表达。）
//
// 断点方案：**不用 JS 读窗口宽度**（会有首屏水合 / 抖动问题），而是由 CSS / 一个开关决定
// 显示哪一份 —— 下钻列表 `md:hidden`（它的类名一个字没改），树形的显示类见 `TREE_SHOWN`。
// 两种形态**共用同一份数据**（`visibleNodes`）与**同一批行组件**
// （`NodeHeaderContent` / `NodeHabitLines` / `RowMenu` / `NodeActionsMenu` / `HabitActionsMenu`）。

/**
 * 下钻形态下**一行节点**。
 *
 * 与宽屏 `NodeRow` 的区别只有三处（其余全部共用）：
 *  1. 没有 `▸/▾`（下钻不折叠）；
 *  2. 有子项时整行可点 = **进入**，右侧给出"进入"提示（`›` + `N 个子项`）；
 *  3. 有子项时**不显示**该节点自己的打卡任务（它的子项才是内容）——
 *     叶子节点则**就地**显示它的打卡任务（同一个 `NodeHabitLines`）。
 */
function DrillRow({
  node,
  ctx,
  onOpen,
  className,
}: {
  node: NodeSummary;
  ctx: TreeContext;
  /** `null` = 这一行不可点（叶子节点：没有可进入的内容，就地显示它的打卡任务）。 */
  onOpen: (() => void) | null;
  /** 与宽屏顶级行同样的用法：第 2 行起加上一条很轻的分割线（`border-t` + `pt-1`）。 */
  className?: string;
}) {
  const isLeaf = (node.children?.length ?? 0) === 0;
  const canEdit = canEditContent(ctx.actor, ownershipOf(node));
  const archived = isArchived(node);
  const showCheckins = node.status === 'active' && !archived;
  const childCount = node.children?.length ?? 0;
  const canAddChild = !archived && ctx.canCreateHere && node.depth < MAX_NODE_DEPTH;
  const canAddHabit = isLeaf && showCheckins;

  /**
   * 行容器的公共类名。**必须同时用在 `<button>` 与 `<div>` 两种形态上**
   * （有子项＝可进入＝button，叶子＝div），否则两行的对齐会差一像素。
   *
   * `relative` 是给打开状态下的「⋯」浮层用的：见下方 `RowMenu` 的 `md:static`
   * （窄屏里那个浮层是 `fixed`，`relative` 在这里只是给 row 一个定位上下文，
   * 不影响 `fixed` 的定位）。
   *
   * 底色**按 depth 取同一张 `LEVELS` 表**（与宽屏树形完全同源），这样同一条行在两种
   * 形态下的样式一致。下钻一次只显示一层、同一层的行都是兄弟（`depth` 相同），
   * 所以**一屏之内的底色依旧是统一的**，只是"每级换一个色相"，不会变成深浅不一的块。
   *
   * ⛔⛔ **hover / active 改用"与底色无关的中性叠加"`bg-slate-900/5`（一次性根治）**：
   *  这个问题已经出现过三次 —— 每次换层级底色，都要重新挑一个"在所有底色上都看得见"
   *  的悬停色。之前用的是实心 `slate-*`（同一绝对色值套在三档深浅底上，第 1 层几乎看不出来、
   *  第 3 层又一跳一大截），后来改成半透明 `slate-300/60`（灰调叠加，在灰底上成立，
   *  但底色一变成 `indigo` / `sky` / `amber`，压上去就会**发灰、发脏**，和明亮色系打架）。
   *  现在改成 `slate-900/5`：它是**中性黑**的低透明度叠加，压在**任何**浅色底上都只是
   *  "略微压深一点"，不引入任何新色相。
   *  好处是它**三层通用、不需要每层各调一次** —— 以后无论层级底色怎么换，这一行都不用再动。
   *  ⚠️ 这里用半透明**不违反**上面"层级底色必须不透明"的规矩：那条规矩针对的是
   *  **层级底色**（它会嵌在父级底色里、会被再叠一次，所以必须不透明）；hover / active 是
   *  **临时交互反馈**，不表达层级，叠在谁身上都只是"这一行被指着 / 被按下"，语义单一。
   */
  const rowClass = cn(
    'relative flex w-full flex-wrap items-start gap-2 rounded-lg px-2 py-2 text-left',
    levelStyle(node.depth).row,
    onOpen !== null && 'transition-colors hover:bg-slate-900/5 active:bg-slate-900/5',
  );

  const inner = (
    <>
      {/*
        左侧 4px 状态色条（颜色映射只在 statusBarClass / STATUS_ACCENT 一处）。
        进行中时它是**透明占位**，保证所有行的文字左边缘对齐。
      */}
      <span aria-hidden="true" className={statusBarClass(node.status, archived)} />
      <div className="min-w-0 flex-1">
        <NodeHeaderContent node={node} archived={archived} />
      </div>
      {/*
        "进入"提示：只在**有子项**时出现。`›` 是方向提示，`N 个子项`是数量提示 ——
        两个一起给，因为只有箭头时用户不知道里面有多少东西、值不值得点进去。
        ⚠️ 窄屏行是 `flex-wrap`：窄屏挤不下时这块会**换到下一行**（`w-full justify-end`），
        而不是把节点名压成"一个字一行"（见 `min-w-0` 那段注释）。
        这个换行只在窄屏会发生（宽屏根本不渲染这一块）。
      */}
      {childCount > 0 && (
        <span className="flex w-full shrink-0 items-center justify-end gap-1 text-xs font-medium text-brand-600">
          <span>{childCount} 个子项</span>
          <span aria-hidden="true">›</span>
        </span>
      )}
    </>
  );

  return (
    <li className={cn(className, archived && 'opacity-70')}>
      <div className="flex items-stretch gap-2">
        {/*
          "点行进入"。⚠️ 可点区域**只包住状态色条 + 文字 + 进入提示**，不含右侧的「⋯」：
          「⋯」是这一行的**兄弟节点**（`RowMenu` 在下面的 flex 里），所以点它天然不会
          命中"进入"（不是靠 `stopPropagation` 兜住，而是结构上就不在可点区域里）。
          父行用 `<button>` 而不是给 `<div>` 挂 onClick：键盘 Enter/Space 直接可用。
        */}
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            aria-label={`进入「${node.name}」`}
            className={rowClass}
          >
            {inner}
          </button>
        ) : (
          <div className={rowClass}>{inner}</div>
        )}

        {/*
          操作区：与宽屏**完全同一个组件、同一套条件**（权限不足整块不渲染）。
          `pr-0 md:pr-9`：宽屏保持原有的右侧留白；窄屏这个盒子在菜单打开时会变成
          `absolute right-2`（见 `RowMenu`），`pr-9` 会把「⋯」从右边缘再推开 36px，
          所以窄屏要归零 —— 右边的 8px 由 `right-2` 提供。
        */}
        {canEdit && (
          <div className="flex shrink-0 items-start pr-0 md:pr-9">
            <NodeActionsMenu
              node={node}
              archived={archived}
              open={ctx.openMenuId === nodeMenuId(node.id)}
              canAddChild={canAddChild}
              canAddHabit={canAddHabit}
              onToggle={() =>
                ctx.openMenuId === nodeMenuId(node.id)
                  ? ctx.menuClose()
                  : ctx.menuOpenAt(nodeMenuId(node.id))
              }
              onClose={ctx.menuClose}
              onAddChild={() => ctx.onAddChild(node)}
              onAddHabit={() => ctx.onAddHabit(node)}
              onPause={() => ctx.onPause(node)}
              onResume={() => ctx.onResume(node)}
              onComplete={() => ctx.onComplete(node)}
              onStop={() => ctx.onStop(node)}
              onReopen={() => ctx.onReopen(node)}
              onEdit={() => ctx.onEdit(node)}
              onArchive={() => ctx.onArchive(node)}
              onDelete={() => ctx.onDelete(node)}
            />
          </div>
        )}
      </div>

      {/*
        叶子节点：**就地**显示它的打卡任务 —— 用与宽屏同一个 `NodeHabitLines`，
        所以每条任务一个「⋯」、菜单项、id 约定（`h:<habitId>`）都完全一致。
        窄屏的节点行没有缩进层级，所以这一块不再左移 `ml-6`（宽屏那 24px 是为缩进留的）。
        `pl-1`：让打卡任务行的左边缘比节点名略往里一点，读得出从属关系。
        `pr-9 md:pr-0`：窄屏的「⋯」是 `absolute right-2`（不占位），所以在右侧留出
        36px，免得它压住打卡任务这一行自己的「⋯」；宽屏的「⋯」在正常流里，不留。
        有子项的行**不**显示打卡任务 —— 那类节点按契约本来也不该有（孩子端只显示叶子）。
      */}
      {isLeaf && showCheckins && (
        <div className="mt-1 pl-1 pr-9 md:pr-0">
          {(node.habits ?? []).length === 0 ? (
            <p className="text-xs text-slate-400">
              {canEdit
                ? '还没有打卡任务 —— 打开这一行右侧的「⋯」，选「加打卡任务」，给它加一条「每天做什么」。'
                : '还没有打卡任务。'}
            </p>
          ) : (
            <NodeHabitLines node={node} ctx={ctx} canEdit={canEdit} />
          )}
        </div>
      )}
    </li>
  );
}

/**
 * 面包屑：`全部 / 提升语文成绩 / 提升写作能力`。
 *
 * 数据结构：**一个从根到当前焦点的节点数组**（页面里由 `visibleNodes` +
 * `parentId` 逐级向上算出来），不是字符串 —— 每一段都带着自己的 id，
 * 所以"点哪一段就回到哪一层"不需要再做任何解析。
 *
 * 可点范围：**每一段都可点（回到该层）**，只有最后一段（当前层）是普通文字。
 * 刻意**不加"返回上一层"按钮**：面包屑本身已经能逐级返回，用户明确抱怨过按钮多。
 */
function DrillBreadcrumb({ trail, onFocus }: { trail: NodeSummary[]; onFocus: (id: string) => void }) {
  return (
    <nav aria-label="返回路径" className="min-w-0">
      <ol className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
        <li className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onFocus('')}
            className="rounded px-1 py-1 font-medium text-brand-600 hover:bg-brand-50"
          >
            全部
          </button>
        </li>
        {trail.map((crumb, index) => {
          const isCurrent = index === trail.length - 1;
          return (
            <li key={crumb.id} className="flex min-w-0 items-center gap-1">
              <span aria-hidden="true" className="text-slate-300">
                /
              </span>
              {isCurrent ? (
                // 当前层：普通文字，不可点（点了也没有意义）
                <span aria-current="page" className="min-w-0 truncate px-1 py-1 font-semibold text-slate-700">
                  {crumb.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onFocus(crumb.id)}
                  className="min-w-0 truncate rounded px-1 py-1 font-medium text-brand-600 hover:bg-brand-50"
                >
                  {crumb.name}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}


// ---------------------------------------------------------------- 页面

export function ParentNodesPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const studentQuery = useStudent(id);
  const actor = useParentActor(studentQuery.data?.permission ?? null);
  const canEditAnything = canCreate(actor);

  /**
   * 节点树查询：**不传 `kind`** —— 后端省略 `kind` 时返回**全部**节点（一棵树），
   * 历史上 kind='task' 的老节点也在里面，所以它们照常显示。
   * 页面上已经没有目标 / 任务两个 Tab，也就没有"当前 kind"这个状态了。
   */
  const treeQuery = useNodeTree(id);
  const nodeMutations = useNodeMutations(id);

  const [editing, setEditing] = useState<NodeSummary | null>(null);
  const [form, setForm] = useState<NodeFormState | null>(null);
  /** 孩子端只读预览（「看看孩子今天的清单」那个按钮）。 */
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** 服务端拒绝时返回的"越界子项清单"（见契约 1.1）。 */
  const [conflictChildren, setConflictChildren] = useState<
    Array<{ id: string; name: string; detail: string }>
  >([]);
  const [pendingDelete, setPendingDelete] = useState<NodeSummary | null>(null);
  /** 打卡任务表单：`habit === null` 表示新建，否则是编辑。 */
  const [habitModal, setHabitModal] = useState<{ node: NodeSummary; habit: Habit | null } | null>(null);
  /** 待删除的打卡任务：删除必须先经确认（见下方弹窗文案）。 */
  const [pendingHabitDelete, setPendingHabitDelete] = useState<Habit | null>(null);
  /**
   * 待确认的状态动作（暂停 / 停止 / 恢复 / 重新开启）与归档动作。
   *
   * 为什么要"先确认"：这四个动作都会**向下影响子目标** —— 手机上一次误触
   * 可能把孩子十几个子目标一起停掉，而恢复时又要区分"被父级带下来的"。
   * 单条确认 + 一句人话说明影响，比事后补救便宜得多。
   */
  const [pendingStatus, setPendingStatus] = useState<{
    node: NodeSummary;
    action: 'pause' | 'resume' | 'stop' | 'reopen';
  } | null>(null);
  /** 待确认的归档 / 取消归档（同样先确认，避免误把整棵树收起来）。 */
  const [pendingArchive, setPendingArchive] = useState<NodeSummary | null>(null);

  /**
   * 筛选 chips：**多选、可叠加**，默认「进行中 + 暂停」（还需要关注的）。
   * 见上方 `FILTER_CHIPS` 的注释：为什么用 chips 而不是 Tab。
   */
  const [filters, setFilters] = useState<Set<StatusFilter>>(new Set(DEFAULT_FILTERS));

  const toggleFilter = (value: FilterChip) => {
    setFilters((prev) => {
      // 「全部」= 清空筛选，让整棵树原样出现在同一列表里
      if (value === 'all') return new Set<StatusFilter>();
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  /**
   * 当前打开的那个「⋯」菜单的 id（`null` = 都关着）。
   *
   * 单一状态而不是每行一个 boolean：后者会出现多个菜单同时展开
   * （点开第二个时第一个仍然开着），同一时刻**只允许一个**。
   * 另外它只存 id，不存坐标/内容 —— 菜单的定位由触发器自己算。
   *
   * 它**同时覆盖节点行与打卡任务行**（节点 `n:<nodeId>`、打卡任务 `h:<habitId>`，
   * 见 `nodeMenuId` / `habitMenuId` 的约定）；两类共用一个状态，所以
   * "打开打卡任务的菜单 → 节点菜单自动关"和反向的情形都不需要额外代码。
   */
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const nodes = treeQuery.data?.nodes ?? [];
  const notReady = treeQuery.data?.notReady ?? false;

  /**
   * 展开的节点 id 集合。**默认空集合 = 默认全部收起**（本轮按用户新要求改的）。
   *
   * ⚠️ 这个状态**只有用户点 ▸/▾ 时才会变**（`toggle`）—— 页面上没有任何
   * "看到数据就灌一遍"的 effect。这正是不回归的关键：
   *  1. 树数据每次刷新（invalidate / refetch / 窗口重新聚焦）都会让
   *     `treeQuery.data` 变成新对象，只要没人去 `setExpanded`，用户手动展开
   *     的分支就会原样保留，不会"自己弹开或塌回去"；
   *     （此前那个"首次把全部节点 id 灌进 expanded"的 effect 已随需求删除，
   *     它的副作用恰恰是刷新后覆盖用户状态，所以删掉它反而更安全。）
   *  2. 页面只有一棵树（不再有目标 / 任务两个 Tab），所以"切 Tab 会不会清空展开状态"
   *     这个问题本身已经不存在了。
   */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (nodeId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

  /**
   * **窄屏下钻的焦点节点 id**（`''` = 根，也就是"看所有顶层节点"）。
   *
   * ⚠️ 它**只服务窄屏**：宽屏依旧是 `expanded` 那套树形展开，两者互不影响。
   * 而且它是一个**不受控、默认根**的纯 UI 状态 —— 没有 effect 去灌它，
   * 数据刷新（invalidate / refetch）不会让它跳回根；只有当焦点节点**在当前视图里
   * 不存在了**（被删掉、或被筛选排除）才会由下面的 `drillMemo`
   * 自动落回根，不需要额外代码。
   *
   * 切筛选**不清空**它：节点 id 全局唯一，与 `expanded` 的处理口径一致
   * （"你手动走到哪一层"应该被记住）。
   */
  const [drillFocusId, setDrillFocusId] = useState<string>('');

  const today = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  /** 扁平化，供"父节点选择"下拉使用。 */
  const flatNodes = useMemo(() => {
    const out: NodeSummary[] = [];
    const walk = (list: NodeSummary[]) => {
      for (const node of list) {
        out.push(node);
        if (node.children) walk(node.children);
      }
    };
    walk(nodes);
    return out;
  }, [nodes]);

  /**
   * 筛选后的展示树。
   *
   * 口径（刻意如此）：
   *  1. 先按选中的 chips 挑出**命中**的节点；
   *  2. 再把命中节点的**祖先**补回来当上下文 —— 否则一个"进行中的子目标"
   *     会因为它的父目标被筛掉而突然变成第一级，缩进和归属全乱；
   *  3. 一次都没选（或选了「全部」）时就是整棵树。
   *
   * 计数来自**全量**节点（不受筛选影响），这样 chip 上的数字不会因为
   * "选了进行中"就只剩进行中的数字。
   */
  const filterCounts = useMemo(() => {
    const counts: Record<FilterChip, number> = {
      all: flatNodes.length,
      active: 0,
      paused: 0,
      completed: 0,
      stopped: 0,
      archived: 0,
    };
    for (const node of flatNodes) {
      for (const key of filterKeysOf(node)) counts[key] += 1;
    }
    return counts;
  }, [flatNodes]);

  /** 按 chips 过滤后的展示树（浅层副本列表，缩进仍用后端给的 depth）。 */
  const visibleNodes = useMemo(() => {
    if (filters.size === 0) return nodes;
    const all = flattenNodes(nodes);
    const byId = new Map(all.map((node) => [node.id, node]));
    // 命中集合 + 逐级向上的祖先（祖先只是"上下文"，不代表它自己符合筛选）
    const keep = new Set<string>();
    for (const node of all) {
      if (filterKeysOf(node).some((key) => filters.has(key))) {
        keep.add(node.id);
        let parentId = node.parentId;
        // 防御性上限：数据库约束最多三级，这里用 8 兜底避免脏数据造成死循环
        for (let hop = 0; parentId && hop < 8; hop += 1) {
          keep.add(parentId);
          parentId = byId.get(parentId)?.parentId ?? null;
        }
      }
    }
    return rebuildTree(all.filter((node) => keep.has(node.id)));
  }, [nodes, filters]);

  /**
   * 是否"有内容可展示"。
   *
   * ⚠️ 这里必须与 `visibleNodes` 用**同一个口径**，否则会出现一个很难查的现象：
   * 点「全部」（把筛选集合清空）后，`visibleNodes` 明明返回了整棵树，
   * 但本判定写成 `filters.has(key)` —— 空集合时恒为 false，于是界面显示
   * "当前筛选下没有内容"，看起来就是"点全部什么都没了"。
   *
   * 教训：同一个空集合语义不要在两个地方各写一遍。
   */
  const anyNodeMatches = useMemo(() => {
    if (filters.size === 0) return flatNodes.length > 0; // 「全部」= 有节点就有内容
    return flatNodes.some((node) => filterKeysOf(node).some((key) => filters.has(key)));
  }, [flatNodes, filters]);

  /**
   * **窄屏下钻列表**：当前焦点节点的**直接子项**（焦点是根时就是顶层节点）。
   *
   * 三个刻意如此的口径：
   *  1. 数据源是 `visibleNodes`（已经被 chips 筛过的那棵树）—— 也就是说
   *     **筛选在窄屏的语义是"过滤当前这一层"**，与宽屏"过滤整棵树的当前可见层"
   *     用的是同一份过滤结果，没有第二套过滤逻辑，也不做跨层搜索；
   *  2. 焦点节点自己必须"在筛选结果里存在"，否则落回根 —— 用户点进某一层后
   *     再改筛选、把那一层筛掉时，不会停在一个看不见的层上（否则列表会莫名其妙
   *     变成空的，还看不出为什么）；
   *  3. 焦点节点的子项里，只有 `visibleNodes` 里留下来的才会显示（同上）。
   *
   * ⚠️ 空态：焦点在某一层、但它的子项被筛光了 —— 这是**合法状态**，
   * 页面用"当前筛选下没有内容"的既有空态文案兜住（见下面的渲染分支）。
   */
  const drill = useMemo(() => {
    const roots = visibleNodes;
    const focused = drillFocusId ? findNode(roots, drillFocusId) : null;
    // 焦点节点在当前筛选下不存在（被筛掉 / 被删掉 / 换了 Tab）→ 自动落回根
    const items = focused ? (focused.children ?? []) : roots;
    const trail = focused ? trailOf(roots, focused.id) : [];
    return { items, trail };
  }, [visibleNodes, drillFocusId]);

  const openCreate = (parent: NodeSummary | null) => {
    setEditing(null);
    setFormError(null);
    setConflictChildren([]);
    // 只有目标这一种类型了：新建一律按 'goal' 的默认值起表单
    // （'goal' 的默认时间范围是「长期」，所以这里传 'goal' 而不是某个可变状态）
    setForm(emptyForm('goal', today, parent?.id ?? null));
  };

  const openEdit = (node: NodeSummary) => {
    setEditing(node);
    setFormError(null);
    setConflictChildren([]);
    setForm(nodeToForm(node));
  };

  /**
   * 前端的时间边界提示（契约 1.1 要求"前端也要提示，但以服务端拒绝为准"）。
   * 这里只做能确定的判断：子级的日期是否落在上级范围内。
   */
  const frontendRangeWarning = useMemo(() => {
    if (!form || !editing) return null;
    const parent = flatNodes.find((node) => node.id === editing.parentId);
    if (!parent || parent.isLongTerm) return null;
    const payload = formToPayload(form);
    if (payload.isLongTerm) {
      return `上级「${parent.name}」有截止日期 ${parent.endDate ?? '—'}，把子项设成「长期」会超出它的范围。`;
    }
    const end = payload.endDate ?? payload.startDate ?? '';
    if (parent.endDate && compareDate(end, parent.endDate) > 0) {
      return `子项截止日期 ${end} 晚于上级「${parent.name}」的截止日期 ${parent.endDate}，服务端会拒绝保存。`;
    }
    if (payload.startDate && compareDate(payload.startDate, parent.startDate) < 0) {
      return `子项开始日期 ${payload.startDate} 早于上级「${parent.name}」的开始日期 ${parent.startDate}，服务端会拒绝保存。`;
    }
    return null;
  }, [form, editing, flatNodes]);

  /** 统一的写操作错误处理：把"越界子项清单"提取出来单独展示。 */
  const handleWriteError = (err: unknown) => {
    if (err instanceof ApiError && err.code === 'NODE_DATE_OUT_OF_PARENT_RANGE') {
      const parsed = parseOutOfRangeChildren(err.details);
      setConflictChildren(parsed.items);
      setFormError(
        parsed.items.length > 0
          ? '保存被服务端拒绝了：下面这些子项会超出新的时间范围，请先调整它们。'
          : `${err.message}${describeDetails(err.details) ? `（${describeDetails(err.details)}）` : ''}`,
      );
      return;
    }
    setConflictChildren([]);
    setFormError(toErrorMessage(err));
  };

  const submit = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      setFormError('请填写名称');
      return;
    }
    setFormError(null);
    const payload = formToPayload(form);
    try {
      if (editing) {
        await nodeMutations.update.mutateAsync({ nodeId: editing.id, payload });
      } else {
        await nodeMutations.create.mutateAsync({
          ...payload,
          // 界面上只有「目标」了，新建出来的节点固定是 'goal'
          kind: 'goal',
          parentId: form.parentId ? form.parentId : null,
        });
      }
      toast.showToast(editing ? '已保存' : `已创建${NODE_LABEL}`, 'success');
      setForm(null);
      setEditing(null);
    } catch (err) {
      handleWriteError(err);
    }
  };

  /**
   * 状态变更（暂停 / 恢复 / 完成 / 停止 / 重新开启）。
   *
   * 两类动作的处理**刻意不同**：
   *  - 会向下传播的（暂停 / 停止 / 恢复 / 重新开启）在调用前先弹确认，
   *    确认框里写清"子目标会跟着怎样"（见 `confirmStatus` 的文案表）；
   *  - 完成**不弹确认**（它不会改子目标），但可能被服务端拒绝 ——
   *    "还有 N 个子目标未完成"是后端给出的中文提示，必须原样展示，
   *    绝不能吞成一句笼统的"操作失败"。
   */
  const runStatusAction = async (
    node: NodeSummary,
    action: 'pause' | 'resume' | 'complete' | 'stop' | 'reopen',
  ) => {
    try {
      await nodeMutations.setStatus.mutateAsync({ nodeId: node.id, action });
      toast.showToast(`${STATUS_ACTION_DONE[action]}「${node.name}」`, 'success');
      return true;
    } catch (err) {
      // 完成被拒（还有未完成的子目标）时，把后端的原话完整展示出来
      toast.showToast(toErrorMessage(err), 'error');
      return false;
    }
  };

  /** 归档 / 取消归档：状态与归档是**两个维度**，所以任意状态都能归档。 */
  const runArchive = async (node: NodeSummary) => {
    const archived = isArchived(node);
    try {
      await nodeMutations.setArchived.mutateAsync({ nodeId: node.id, archived: !archived });
      toast.showToast(archived ? `已取消归档「${node.name}」` : `已归档「${node.name}」`, 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await nodeMutations.remove.mutateAsync(pendingDelete.id);
      toast.showToast('已删除', 'success');
      setPendingDelete(null);
    } catch (err) {
      setPendingDelete(null);
      // 有打卡记录时服务端明确要求"改为归档"
      if (err instanceof ApiError && err.code === 'NODE_HAS_CHECKINS') {
        toast.showToast('这条内容下已有打卡记录，不能删除；请改用「归档」', 'error');
      } else {
        toast.showToast(toErrorMessage(err), 'error');
      }
    }
  };

  /**
   * 打卡任务的写操作。
   *
   * 节点 id 传当前表单所属的节点：它只用于让 hooks 失效 `['nodes', id, 'habits']`
   * 这个缓存键（本页不读它）。界面上打卡任务的显示来自节点树，
   * 由 hooks 里统一的 `['students', studentId, 'nodes']` 失效刷新 ——
   * 停用 / 启用 / 删除走的是同一条路径，所以传 null 也不会让界面停在旧数据上。
   */
  const habitMutations = useHabitMutations(id, habitModal?.node.id ?? null);

  /** 停用 / 启用：停用会保留全部历史统计（界面上要写明，避免家长以为等于删除）。 */
  const toggleHabit = async (habit: Habit) => {
    try {
      await habitMutations.update.mutateAsync({
        habitId: habit.id,
        payload: { active: !habit.active },
      });
      toast.showToast(habit.active ? '已停用（历史统计保留）' : '已启用', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doDeleteHabit = async () => {
    if (!pendingHabitDelete) return;
    const habit = pendingHabitDelete;
    try {
      await habitMutations.remove.mutateAsync(habit.id);
      toast.showToast('已删除打卡任务', 'success');
      setPendingHabitDelete(null);
    } catch (err) {
      setPendingHabitDelete(null);
      // 已有打卡记录时服务端会拒绝（HABIT_HAS_CHECKINS），
      // 它的中文说明本身就在引导家长改用「停用」，直接展示即可。
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const ctx: TreeContext = {
    actor,
    canCreateHere: canEditAnything,
    onAddChild: openCreate,
    onEdit: openEdit,
    // 向下传播的动作一律先弹确认（见 STATUS_CONFIRM 的文案表）
    onPause: (node) => setPendingStatus({ node, action: 'pause' }),
    onResume: (node) => setPendingStatus({ node, action: 'resume' }),
    // 完成不会改子目标 → 不弹确认，但被拒时要展示后端的中文提示
    onComplete: (node) => void runStatusAction(node, 'complete'),
    onStop: (node) => setPendingStatus({ node, action: 'stop' }),
    onReopen: (node) => setPendingStatus({ node, action: 'reopen' }),
    onArchive: setPendingArchive,
    onDelete: setPendingDelete,
    onAddHabit: (node) => setHabitModal({ node, habit: null }),
    onEditHabit: (node, habit) => setHabitModal({ node, habit }),
    onToggleHabit: (habit) => void toggleHabit(habit),
    onDeleteHabit: setPendingHabitDelete,
    expanded,
    toggle,
    openMenuId,
    // 打开某个节点的菜单时直接覆盖上一个 id —— 一句赋值就实现了"只开一个"
    menuOpenAt: setOpenMenuId,
    menuClose: () => setOpenMenuId(null),
  };

  return (
    <AppShell
      kind="parent"
      title={studentQuery.data ? `${studentQuery.data.nickname} · ${NODE_LABEL}` : NODE_LABEL}
      headerAction={
        canEditAnything ? (
          <Button size="sm" onClick={() => openCreate(null)}>
            新建{NODE_LABEL}
          </Button>
        ) : (
          <Badge tone="neutral">仅查看</Badge>
        )
      }
    >
      <div className="space-y-4">
        {/* ---------- 归属说明：避免家长误操作孩子建的内容 ---------- */}
        <Card className="bg-slate-50">
          <p className="text-sm text-slate-600">
            这里是孩子的「目标」，最多三级：大目标 → 小目标 → 再分一级。
            <br />
            打卡任务只能挂在<span className="font-medium">最底下一层</span>（没有子目标的那一级），
            孩子就是在那里打卡。
          </p>
          {!canEditAnything && (
            <p className="mt-1 text-sm text-amber-700">
              你是「仅查看」权限，可以浏览但不能增删改。
            </p>
          )}
        </Card>

        {/* ---------- 状态筛选 chips（多选、可叠加）----------
             刻意不用 Tab：Tab 会把数据切碎（一次只能看一类，看不到全局，
             切来切去还会丢失上下文）。筛选是**叠加在同一棵树上**的：
             选几类就同时看几类，未选中的只是不显示，树的结构与缩进不变。
             默认选中「进行中 + 暂停」= 还需要关注的。 */}
        {/*
          ⚠️ **换行对齐**（2026-10-31 用户要求的最终形态）：
          · 「筛选：」与第一个 chip（「全部」）**在同一行**（和原来一样）；
          · chips 换行之后，第二行起**与第一个 chip 左对齐**（不再顶到"筛选："下面）。

          做法：外层 `relative` + 左内边距（= 标签宽度），标签绝对定位在第一行行首，
          chips 容器从内边距处开始换行 —— 于是**每一行 chips 都对齐第一个 chip**，
          而第一行看起来仍然紧跟在「筛选：」后面。
          语义没变（仍是 `role="group"` + `aria-pressed` 的叠加筛选）。
        */}
        <div role="group" aria-label="按状态筛选" className="relative pl-11">
          <span className="absolute left-0 top-1 text-xs text-slate-500">筛选：</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTER_CHIPS.map((chip) => {
              const selected = chip.value === 'all' ? filters.size === 0 : filters.has(chip.value);
              const count = filterCounts[chip.value];
              return (
                <button
                  key={chip.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleFilter(chip.value)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {chip.label}({count})
                </button>
              );
            })}
          </div>
          {/* 2026-10-30 用户要求：去掉「显示全部状态」按钮与那句筛选说明（点「全部」即可看全部）。 */}
        </div>

        {/* ---------- 树 ---------- */}
        {treeQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : treeQuery.isError ? (
          <ErrorState
            message={toErrorMessage(treeQuery.error)}
            onRetry={() => void treeQuery.refetch()}
          />
        ) : notReady ? (
          <NotReadyState feature={NODE_LABEL} />
        ) : nodes.length === 0 ? (
          <EmptyState
            icon="🎯"
            title={`还没有${NODE_LABEL}`}
            description="先给孩子定一个方向，比如「这个学期把阅读习惯养起来」；需要的话再往下分一两级。"
            action={
              canEditAnything ? (
                <Button className="mt-2" onClick={() => openCreate(null)}>
                  点这里创建第一个{NODE_LABEL}
                </Button>
              ) : undefined
            }
          />
        ) : !anyNodeMatches ? (
          // 注意：这里**不是**"还没有目标"，而是"筛掉了" —— 两种文案对用户完全不同
          <EmptyState
            icon="🔍"
            title="当前筛选下没有内容"
            description="上面这些筛选是叠加的。把「完成 / 停止 / 已归档」也勾上，或者点「全部」看看。"
            action={
              <Button className="mt-2" variant="secondary" onClick={() => setFilters(new Set())}>
                显示全部
              </Button>
            }
          />
        ) : (
          /*
            形态（本轮：**所有断点都用树形**）：
              · 树形 —— **唯一**渲染的一份，窄屏（< md）也显示（`TREE_SHOWN` === `block`）；
              · 逐层下钻 —— 代码全在，但被 `DRILL_ENABLED`（`false`）挡在渲染之外。
            改回"窄屏下钻 / 宽屏树形"只需把 `DRILL_ENABLED` 改成 `true` 一行。

            ⚠️ 为什么下钻用的是**条件渲染**而不是 `hidden`：菜单面板是 `position: fixed`，
            父级的 `display:none` 藏不住它。两份若都进 DOM，"同一个节点的 `openMenuId`
            会让两份菜单同时打开" —— 下钻那一份的面板类名是 `md:hidden`，
            在窄屏恰恰是**可见**的（这正是 `MenuVariantContext` 要防的 bug）。
            **只渲染一份**时这个问题从根上不存在，而那一套机制**保留不动**
            （它是为恢复下钻准备的：恢复后两份又都进 DOM，机制照旧生效）。

            两份渲染共用同一份数据（`visibleNodes`）与同一批行组件
            （`NodeHeaderContent` / `NodeHabitLines` / `RowMenu` / `NodeActionsMenu` /
            `HabitActionsMenu`），只是"渲染哪些行 + 怎么导航"不同。
          */
          <>
            {/* ---------- 窄屏：逐层下钻（**暂时停用**：把 DRILL_ENABLED 改成 true 即恢复） ---------- */}
            {DRILL_ENABLED && (
              <div className="rounded-2xl border border-slate-200 bg-white p-2 md:hidden">
                {/*
                  面包屑：`全部 / 提升语文成绩 / 提升写作能力`，每一段可点（回到该层），
                  当前层是普通文字。**刻意不加"返回上一层"按钮** —— 面包屑已经够用，
                  用户明确抱怨过按钮多。焦点在根时只有「全部」一段（它同时也是当前层，
                  所以那时它是普通文字而不是链接）。
                */}
                <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 px-1 pb-2">
                  {drill.trail.length === 0 ? (
                    <span aria-current="page" className="px-1 py-1 text-xs font-semibold text-slate-700">
                      全部
                    </span>
                  ) : (
                    <DrillBreadcrumb trail={drill.trail} onFocus={setDrillFocusId} />
                  )}
                  <span className="ml-auto text-xs text-slate-400">
                    共 {drill.items.length} 项
                  </span>
                </div>

                {/*
                  当前层被筛选清空：这是**筛选**的结果，不是"没有内容"（两者文案完全不同，
                  见上面那个空态）。用与宽屏同一个「当前筛选下没有内容」口径 + 同一个
                  「显示全部」动作。
                */}
                {drill.items.length === 0 ? (
                  <EmptyState
                    icon="🔍"
                    title="当前筛选下没有内容"
                    description="这一层在当前筛选下是空的。上面这些筛选是叠加的，把「完成 / 停止 / 已归档」也勾上，或者点「全部」看看。"
                    action={
                      <Button className="mt-2" variant="secondary" onClick={() => setFilters(new Set())}>
                        显示全部
                      </Button>
                    }
                  />
                ) : (
                  <ul className="mt-1 space-y-1">
                    {drill.items.map((node, index) => (
                      /* 窄屏这一份：菜单面板也只在窄屏显示（见 `MenuVariantContext`） */
                      <MenuVariantContext.Provider key={node.id} value="narrow">
                        <DrillRow
                          node={node}
                          ctx={ctx}
                          // 「点行进入」：**只有有子项的节点可点**（叶子点了没有可进入的内容，
                          // 它就地显示自己的打卡任务，见 DrillRow）。传 null 表示不可点。
                          onOpen={
                            (node.children?.length ?? 0) > 0 ? () => setDrillFocusId(node.id) : null
                          }
                          className={index === 0 ? undefined : 'border-t border-t-slate-100 pt-1'}
                        />
                      </MenuVariantContext.Provider>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/*
              ---------- 树形展开（**现在所有断点都显示**） ----------
              ⚠️ 本轮用户要求"手机上也用这套树形看效果"，所以这一份不再是 `hidden md:block`
              （那是"只在宽屏显示"），而是取 `TREE_SHOWN` —— 目前等于 `block`，
              即**窄屏也显示**。默认全部收起、缩进、连续引导线（挂在 children 的 `<ul>` 上）
              这些都没变；折叠的入口本轮**从"`▸` 那个小按钮"扩成了"整行"**：
              行容器上有 `role="button"` / `tabIndex` / `onClick` / `onKeyDown` /
              `aria-expanded`，`▸/▾` 降级成 `aria-hidden` 的纯视觉指示（见 `NodeRow`）。
              判定与排除规则**只有一处**：`ROW_TOGGLE_EXCLUDE` + `isRowToggleExcluded`。
            */}
            <div className={cn('rounded-2xl border border-slate-200 bg-white p-2 sm:p-3', TREE_SHOWN)}>
              {/*
                整棵树 = **一个区块**：只有最外层还保留"卡片"感（一个白底圆角块）。
                内层一律没有边框、没有独立白底 —— 层级交给**序号圆标 + 每级一个色相的行底色**、
                从属交给**子区域容器上那条连续引导线**、状态的例外交给左侧色条
                （见 NodeRow 上方的注释与 levelStyle / statusBarClass）。
              */}
              {/*
                顶级分支之间**拉开距离**（`space-y-3`）+ 第 2 棵起加一条很轻的分割线：
                分割线画在每个顶级 `<li>` 的**上边**，配 `pt-3`，于是线的上下各有 12px 留白
                （上面是 `space-y-3` 的 margin、下面是这个 `pt-3`）——
                线不会贴住上一棵子树的内容，也不会贴住下一棵子树第一行的底色块，
                每棵子树因此读起来是一个独立的视觉块。
                ⛔ 没有加任何按钮，也没有"全部展开/收起"。
              */}
              <ul className="space-y-3">
                {visibleNodes.map((node, index) => (
                  /*
                    ⚠️ `MenuVariantContext` 包在**每一棵顶级子树**外面（不是包整个 `<ul>`），
                    这样它同时覆盖 `NodeRow` 自己那一行的菜单、它下面所有子行的菜单
                    （递归渲染）、以及每个叶子下面的**每条打卡任务**的菜单 ——
                    三处 `RowMenu` 都来自同一个 Provider，所以"哪一份渲染面板"始终一致。
                  */
                  <MenuVariantContext.Provider key={node.id} value="wide">
                    <NodeRow
                      node={node}
                      ctx={ctx}
                      className={
                        index === 0
                          ? undefined
                          : 'border-t border-t-slate-100 pt-3'
                      }
                    />
                  </MenuVariantContext.Provider>
                ))}
              </ul>
            </div>
          </>
        )}


        {/* ---------- 学生创建的内容单独提示（契约 1.1） ---------- */}
        {flatNodes.some((node) => node.createdByType === 'student') && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardHeader
              title="孩子自己建的内容"
              description="下面这些是孩子在自己的账号里创建的。你有监护权，可以改也可以删，但改动会让孩子看到。"
            />
            <ul className="space-y-1 text-sm text-amber-900">
              {flatNodes
                .filter((node) => node.createdByType === 'student')
                .map((node) => (
                  <li key={node.id}>
                    🎯 {node.name}
                    <span className="ml-2 text-xs text-amber-700">
                      {formatDateRange(node.startDate, node.endDate, node.isLongTerm)}
                    </span>
                  </li>
                ))}
            </ul>
          </Card>
        )}

        {/*
          ---------- 打卡任务的两件工具（2026-10-31 从「打卡任务总览」页搬过来） ----------
          用户要求：把「调整打卡任务顺序」和「看孩子看到的清单」挪到「目标」页，
          顺序是：**调整顺序在上、浏览孩子打卡任务在下**；原来的
          「去打卡任务页看总览」入口整块去掉（那个页面本身也删了）。
        */}
        <TaskOrderCard studentId={id} tree={nodes} canEdit={canEditAnything} />

        <Card>
          <CardHeader
            title="看看孩子今天的清单"
            description="就是孩子端「今天要做什么」的样子（只读，不能代替他打卡）。"
            actionAlign="start"
            action={
              <Button variant="secondary" fullWidth onClick={() => setPreviewOpen(true)}>
                看一下孩子端
              </Button>
            }
          />
        </Card>
      </div>

      {/* 孩子端只读预览（全屏浮层，见 `components/parent/ChildTodayPreview`） */}
      <ChildTodayPreview
        open={previewOpen}
        studentId={id}
        {...(studentQuery.data ? { studentName: studentQuery.data.nickname } : {})}
        onClose={() => setPreviewOpen(false)}
      />

      {/* ---------- 新建 / 编辑弹窗 ---------- */}
      <Modal
        open={form !== null}
        onClose={() => {
          setForm(null);
          setEditing(null);
        }}
        title={editing ? `编辑${NODE_LABEL}` : `新建${NODE_LABEL}`}
        description={editing ? editing.name : '名称、时间范围是必填的两项。'}
        size="lg"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setForm(null);
                setEditing(null);
              }}
            >
              取消
            </Button>
            <Button
              loading={nodeMutations.create.isPending || nodeMutations.update.isPending}
              onClick={() => void submit()}
            >
              {editing ? '保存' : '创建'}
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-4">
            <Input
              label="名称"
              value={form.name}
              maxLength={64}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="例如：这学期养成阅读习惯"
            />
            <Input
              label="备注（可选）"
              value={form.note}
              maxLength={1000}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
            <Input
              label="开始日期"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />

            {/* 三选一：截止日期 / 周期长度 / 长期（方案 1.3 ⑪） */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">时间范围</legend>
              <div className="flex flex-wrap gap-3 text-sm">
                {(
                  [
                    { value: 'end', label: '指定截止日期' },
                    { value: 'period', label: '按周期长度' },
                    { value: 'long', label: '长期' },
                  ] as const
                ).map((option) => (
                  <label key={option.value} className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="date-mode"
                      checked={form.mode === option.value}
                      onChange={() => setForm({ ...form, mode: option.value })}
                      className="h-4 w-4"
                    />
                    {option.label}
                  </label>
                ))}
              </div>

              {form.mode === 'end' && (
                <Input
                  label="截止日期"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              )}

              {form.mode === 'period' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="年"
                      type="number"
                      min={0}
                      max={50}
                      value={form.years}
                      onChange={(e) => setForm({ ...form, years: e.target.value })}
                    />
                    <Input
                      label="月"
                      type="number"
                      min={0}
                      max={600}
                      value={form.months}
                      onChange={(e) => setForm({ ...form, months: e.target.value })}
                    />
                    <Input
                      label="日"
                      type="number"
                      min={0}
                      max={3650}
                      value={form.days}
                      onChange={(e) => setForm({ ...form, days: e.target.value })}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    自动折算出的截止日期：
                    <span className="ml-1 font-medium text-slate-700">
                      {addPeriod(form.startDate, {
                        years: Number(form.years) || 0,
                        months: Number(form.months) || 0,
                        days: Number(form.days) || 0,
                      })}
                    </span>
                    <span className="ml-2 text-slate-400">
                      （1 月 31 日 + 1 个月按 2 月末算，与家长直觉一致）
                    </span>
                  </p>
                </div>
              )}

              {form.mode === 'long' && (
                <p className="text-xs text-slate-500">
                  长期不设截止日期，只累计完成量，不参与「超期」统计。
                </p>
              )}
            </fieldset>

            {/* 父节点（不显示自己，避免成为自己的父级） */}
            {!editing && (
              <Select
                label="上级节点（可选）"
                value={form.parentId}
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                hint={`最多三级；当前已有 ${flatNodes.length} 个节点`}
              >
                <option value="">作为第一级</option>
                {flatNodes
                  // 已归档的不能再挂新子项（归档的语义就是"先收起来"）
                  .filter((node) => node.depth < MAX_NODE_DEPTH && !isArchived(node))
                  .map((node) => (
                    <option key={node.id} value={node.id}>
                      {'　'.repeat(node.depth - 1)}
                      {node.name}
                    </option>
                  ))}
              </Select>
            )}

            {/* 前端时间边界提示 */}
            {frontendRangeWarning && (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                ⚠️ {frontendRangeWarning}
              </p>
            )}

            {/* 服务端拒绝时的越界子项清单 */}
            {conflictChildren.length > 0 && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                <p className="text-sm font-semibold text-rose-800">这些子项会越界，请先调整它们</p>
                <ul className="mt-2 space-y-1 text-sm text-rose-700">
                  {conflictChildren.map((child) => (
                    <li key={child.id} className="flex flex-wrap items-center gap-2">
                      <span aria-hidden="true">↳</span>
                      <span className="font-medium">{child.name}</span>
                      {child.detail && <span className="text-rose-500">{child.detail}</span>}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-rose-600">
                  服务端不会静默级联修改它们 —— 避免误伤孩子已经完成的记录。
                </p>
              </div>
            )}

            {formError && conflictChildren.length === 0 && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {formError}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ---------- 删除确认 ---------- */}
      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="删除确认"
        size="sm"
        footer={
          <div className="flex w-full gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingDelete(null)}>
              取消
            </Button>
            <Button variant="danger" className="flex-1" loading={nodeMutations.remove.isPending} onClick={() => void doDelete()}>
              确认删除
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          要删除「{pendingDelete?.name}」吗？
          {pendingDelete?.createdByType === 'student' && (
            <span className="mt-1 block text-amber-700">这条是孩子自己创建的。</span>
          )}
        </p>
        <p className="mt-2 text-xs text-slate-500">
          如果这条内容下已经有打卡记录，服务端会拒绝删除，请改用「归档」—— 归档会保留历史统计。
        </p>
      </Modal>

      {/* ---------- 状态变更确认（暂停 / 恢复 / 停止 / 重新开启） ----------
           这几个动作都会**向下影响子目标**，所以必须先确认；
           确认框里用一句人话说清影响，而不是只问"确定吗"。 */}
      <Modal
        open={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        title={pendingStatus ? STATUS_CONFIRM[pendingStatus.action].title : ''}
        size="sm"
        footer={
          <div className="flex w-full gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingStatus(null)}>
              取消
            </Button>
            <Button
              className="flex-1"
              loading={nodeMutations.setStatus.isPending}
              onClick={() => {
                if (!pendingStatus) return;
                const { node, action } = pendingStatus;
                void runStatusAction(node, action).then((ok) => {
                  if (ok) setPendingStatus(null);
                });
              }}
            >
              {pendingStatus ? STATUS_CONFIRM[pendingStatus.action].confirm : '确认'}
            </Button>
          </div>
        }
      >
        {pendingStatus && (
          <>
            <p className="text-sm text-slate-600">
              要对「{pendingStatus.node.name}」执行
              <span className="font-semibold">
                {STATUS_CONFIRM[pendingStatus.action].confirm.replace('确认', '')}
              </span>
              吗？
            </p>
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {STATUS_CONFIRM[pendingStatus.action].impact}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {STATUS_CONFIRM[pendingStatus.action].detail}
            </p>
            {/* 先算一次：直接写成两个 countDescendants(...) 调用会重复遍历整棵子树 */}
            {(() => {
              const open = countDescendants(
                pendingStatus.node,
                (child) => !isTerminalNodeStatus(child.status),
              );
              return open > 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  这条下面现有 {open} 个未结束的子目标。
                </p>
              ) : null;
            })()}
          </>
        )}
      </Modal>

      {/* ---------- 归档 / 取消归档确认 ----------
           归档是独立维度（`node.archivedAt`），不进状态标签；但仍会改变列表可见性，
           所以同样先确认。 */}
      <Modal
        open={pendingArchive !== null}
        onClose={() => setPendingArchive(null)}
        title={pendingArchive && isArchived(pendingArchive) ? '取消归档' : '归档确认'}
        size="sm"
        footer={
          <div className="flex w-full gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingArchive(null)}>
              取消
            </Button>
            <Button
              className="flex-1"
              loading={nodeMutations.setArchived.isPending}
              onClick={() => {
                if (!pendingArchive) return;
                const node = pendingArchive;
                void runArchive(node).then(() => setPendingArchive(null));
              }}
            >
              {pendingArchive && isArchived(pendingArchive) ? '确认取消归档' : '确认归档'}
            </Button>
          </div>
        }
      >
        {pendingArchive && (
          <>
            <p className="text-sm text-slate-600">
              要{isArchived(pendingArchive) ? '取消归档' : '归档'}「{pendingArchive.name}」吗？
            </p>
            <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
              归档<strong>不改变进展状态</strong>（「{STATUS_META[pendingArchive.status].label}」
              保持原样），只是把它从默认视图里收起来；历史统计全部保留，随时可以取消归档。
            </p>
          </>
        )}
      </Modal>

      {/* ---------- 打卡任务表单（与「打卡任务」页共用的组件） ---------- */}
      {habitModal && (
        <HabitFormModal
          open
          onClose={() => setHabitModal(null)}
          studentId={id}
          node={habitModal.node}
          editingHabit={habitModal.habit}
        />
      )}

      {/* ---------- 删除打卡任务确认 ----------
           必须先确认：删除会连带丢掉这条任务的历史统计，且不可恢复。
           文案与「打卡任务」页保持一致（那里已有同类确认弹窗）。 */}
      <Modal
        open={pendingHabitDelete !== null}
        onClose={() => setPendingHabitDelete(null)}
        title="删除打卡任务"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setPendingHabitDelete(null)}>
              取消
            </Button>
            <Button
              variant="danger"
              loading={habitMutations.remove.isPending}
              onClick={() => void doDeleteHabit()}
            >
              确认删除
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">要删除「{pendingHabitDelete?.name}」吗？</p>
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          如果<strong>孩子还没在这条任务上打过卡</strong>，它会被彻底删除、从列表里消失
          （系统自动生成的"待办占位"也会一起清掉，不丢任何记录）。
          <br />
          如果<strong>已经打过卡</strong>（完成 / 跳过 / 未完成 / 计时中），服务端会拒绝删除 ——
          那些是孩子的事实记录与积分依据，不能连带删掉。那种情况下请改用「<strong>停用</strong>」：
          停用会保留全部历史统计，需要时还能再启用。
        </p>
      </Modal>
    </AppShell>
  );
}

export { describeRecurrence, formToPayload, nodeToForm, emptyForm };
export type { NodeFormState };
